import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import axios from "axios";
import { Loader, X } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardFooter, CardTitle } from "../Card";
import WizardStep from "../WizardStep";
import WizardNavigation from "../WizardNavigation";
import LoadingScreen from "../../../screens/LoadingScreen";
import { getAuthHeader } from "../../../utils/authHeader";
import { getApiRefusalMessage } from "../../../utils/apiError";
import MediaPickerDialog from "../../post/util/MediaPickerDialog";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { Button } from "../Button";
import CustomToast from "../CustomToast";
import ReconfirmOverlay from "../ReconfirmOverlay";
import BasicInfoStep from "./open-house/BasicInfoStep";
import CaptionedVideoMediaStep, {
    CAPTION_STYLES,
    DEFAULT_BRAND_COLORS,
    type BrandColors,
    type CaptionStyle,
    type ColorSource,
} from "./CaptionedVideoMediaStep";
import {
    type InitialSchedule,
    makeInitialSchedule,
    type MediaCollection,
    type Platform,
    type PlatformContentMap,
    type PlatformGraphicsMap,
    VideoPostType,
} from "./types";
import { ContentLength, SocialPlatforms, type SocialsConnected } from "../../campaign/types";
import type { ListingList, PostRelatedMedia } from "../../post/types";
import type { Assets, AssetType } from "../../../types/asset-type";
import ReviewAndScheduleStep from "./open-house/ContentReviewStep";
import ManualContentStep from "./ManualContentStep";
import type { MediaItem, OpenhousePostProps } from "../../listings/listing/types";
import { getScheduledDateTime, rollToFuture } from "../../post/util/post-time";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/store";
import { Dialog, DialogContent } from "../Dialog";
import ChoosePlatforms from "../../campaign/ChoosePlatforms";
import PlatformSettings from "./PlatformSettings";
import { customStatuses } from "../../../utils/customStatuses";
import { isSharedListing } from "../../../utils/sharedListingFooter";
import PlanLimitReached, { type PlanLimitType } from "../PlanLimitReached";
import PostGraphicPicker from "./PostGraphicPicker";
import ConfirmPopup from "../ConfirmPopup";
import { checkInstagramHashtagViolation } from "../../../hooks/useInstagramHashtagValidation";
import DuplicateYouTubeVideoAlert from "../DuplicateYouTubeVideoAlert";
// MB-1461 — opt-in pre-render caption review: phase-1 editor reuse + the
// in-wizard wait state machine + the phase-1 socket channel.
import { useSocket } from "../../../context/SocketContext";
import CaptionTokenEditor from "../../post/util/CaptionTokenEditor";
import {
    type CaptionToken,
    canSubmit,
    captionEditorReducer,
    toSubmitWords,
    tokensFromWords,
} from "../../post/util/zapcapCaptionEditor";
import {
    initialReviewWaitState,
    reviewWaitReducer,
    shouldWarnOnLeave,
} from "./zapcapReviewWait";
import { cancelZapcapReview } from "./zapcapReviewApi";
import type {
    AdhocBulkReviewResponse,
    ZapcapReviewServerState,
    ZapcapReviewStatusResponse,
} from "../../../types/zapcapReview";
import type { Post } from "../../../types/posts";
import { ContentAudience } from "../../../types/contentAudience";
import { captionPositionField } from "../../../types/captionPosition";
import NicheSetupPrompt from "../../niche/NicheSetupPrompt";
import NicheTopicScriptStep, { type NicheSelection } from "../../niche/NicheTopicScriptStep";
import { useNiche } from "../../niche/useNiche";
import { isNicheVideoListing } from "./adhocListingTypes";
import { bumpNicheScheduledFor, forgetUnchosenSeeded, seedNewNicheSchedules } from "./nicheSchedule";
import { wizardSteps } from "./wizardSteps";
import { isCardOwnPick, presetDialogTitle, presetStartsCaptioned } from "../../add-post/preset";
import { contentAudiencePref, recorderGuidesPref } from "../../../utils/prefCookie";
import { useCaptionPosition } from "./useCaptionPosition";
import useAppSelector from "../../../hooks/useAppSelector";
import { leadGenerationText } from "../../post/util/leadGenerationPost";

// Define the desired platform order to match the workflow
const PLATFORM_ORDER = ['facebook', 'instagram', 'twitter', 'linkedin', 'pinterest', 'google', 'youtube'];

// Authoritative character limits per platform (footer-free, per PRD)
const PLATFORM_LIMITS: Record<string, { limit: number; label: string; requiresMedia: boolean; maxImages: number; maxVideos: number }> = {
    facebook:  { limit: 50000, label: 'Facebook',          requiresMedia: false, maxImages: 10, maxVideos: 1 },
    instagram: { limit: 2200,  label: 'Instagram',         requiresMedia: true,  maxImages: 10, maxVideos: 1 },
    twitter:   { limit: 3000,  label: 'X (formerly Twitter)', requiresMedia: false, maxImages: 4, maxVideos: 1 },
    linkedin:  { limit: 3000,  label: 'LinkedIn',          requiresMedia: false, maxImages: 9,  maxVideos: 1 },
    pinterest: { limit: 500,   label: 'Pinterest',         requiresMedia: true,  maxImages: 1,  maxVideos: 0 },
    google:    { limit: 1500,  label: 'Google My Business',requiresMedia: true,  maxImages: 1,  maxVideos: 0 },
    gmb:       { limit: 1500,  label: 'Google My Business',requiresMedia: true,  maxImages: 1,  maxVideos: 0 },
    youtube:   { limit: 5000,  label: 'YouTube',            requiresMedia: true,  maxImages: 0,  maxVideos: 1 },
};

// Conservative limits used by the AI for content generation (matches EditPostDialog + ContentReviewStep)
const AI_CONTENT_CHAR_LIMITS: Record<string, number> = {
    facebook:  50000,
    instagram: 1600,
    twitter:   3700,
    linkedin:  2100,
    pinterest: 200,
    google:    1000,
    gmb:       1000,
    youtube:   4700,
};

export default function CustomPosts({
    setIsOpen,
    listing,
    open,
    isVideo = false,
    isOpenHouse = false,
    isAdHoc = false,
    getAllPosts,
    preset,
    onBackFromStart,
    niche: pageNiche,
}: OpenhousePostProps) {
    const listingAddress = listing?.formattedAddress || "";
    const [defaultSettings, setDefaultSettings] = useState(true);
    const [hasIndividualChanges, setHasIndividualChanges] = useState(false);

    const [availablePlatforms, setAvailablePlatforms] = useState<Platform[]>(
        []
    );

    const [planLimit, setPlanLimit] = useState<number>(0);
    const [planLimitReached, setPlanLimitReached] = useState<boolean>(false);
    // Which situation opened the dialog: "postSelection" = more platforms
    // picked than posts remaining (step 1), "zapcap"/"post" = the api's 410
    // at submit; planLimitMessage carries the backend's own wording when the
    // 410 named the actual limit.
    const [planLimitType, setPlanLimitType] = useState<PlanLimitType>("post");
    const [planLimitMessage, setPlanLimitMessage] = useState<string | undefined>(
        undefined
    );
    // Platforms whose /post/adhoc already succeeded in THIS wizard session -
    // a retry after a partial failure must not create them again.
    const createdAdhocPlatformsRef = useRef<Set<string>>(new Set());

    // Always support these four platforms.
    const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([]);

    const [chosenPlatforms, setChosenPlatforms] = useState<SocialsConnected>({
        facebook: false,
        instagram: false,
        twitter: false,
        linkedin: false,
        pinterest: false,
        google: false,
        youtube: false,
    });

    const campaignPlatforms = listing?.campaignPlatforms;

    const [currentStep, setCurrentStep] = useState(0);

    // Reset the dialog's scroll position to the top whenever the wizard step
    // changes — otherwise a long step (e.g. CaptionedVideoMediaStep) leaves
    // the next step scrolled mid-way down. Run inside rAF so the new step's
    // DOM is committed first; also walk up to every scrollable ancestor (not
    // just the first) since both the Dialog content and a sub-wrapper can
    // independently be scrolled.
    const wizardCardRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const raf = requestAnimationFrame(() => {
            let el: HTMLElement | null =
                wizardCardRef.current?.parentElement ?? null;
            while (el) {
                const overflowY = window.getComputedStyle(el).overflowY;
                if (overflowY === "auto" || overflowY === "scroll") {
                    el.scrollTop = 0;
                }
                el = el.parentElement;
            }
            wizardCardRef.current?.scrollIntoView?.({
                block: "start",
                behavior: "auto",
            });
        });
        return () => cancelAnimationFrame(raf);
    }, [currentStep]);
    const [previousStep, setPreviousStep] = useState<number | null>(null);
    const [rememberedDefaultSchedule, setRememberedDefaultSchedule] = useState<InitialSchedule | null>(null);
    const [hasScheduleChangesInStep4, setHasScheduleChangesInStep4] = useState<boolean>(false);


    // Track selected media type from MediaPickerDialog for platform filtering.
    // Seeded empty (not "image") so the My Brand media step can tell "no explicit
    // choice yet" apart from "the user picked Images" and apply its video-first
    // default only in the former case (MB-1492).
    const [selectedMediaType, setSelectedMediaType] = useState<string>(
        isVideo ? "video" : ""
    );

    const handleMediaTypeChange = (newType: string) => {
        setSelectedMediaType(newType);
        // Deselect platforms that are incompatible with the new media type
        setChosenPlatforms((prev) => {
            const next = { ...prev };
            if (newType === "image") {
                next.youtube = false;
            } else if (newType === "video") {
                next.pinterest = false;
                next.google = false;
            }
            return next;
        });
    };

    // Video post type selection
    const [videoPostType, setVideoPostType] = useState<VideoPostType | null>(
        null
    );

    const [generalInstructions, setGeneralInstructions] = useState<string>("");
    // Initially enabled
    const [aiEnhance, setAiEnhance] = useState<boolean>(true);
    // When checked in BasicInfoStep (ad-hoc / mybrand flows), forces video-only platform set
    // and swaps the media step for the captioned-video flow.
    // The Add Post page's Captioned Video and My Niche Video cards start captioned; the checkbox is hidden there.
    const [postCaptionedVideo, setPostCaptionedVideo] = useState<boolean>(preset ? presetStartsCaptioned(preset) : false);
    const [captionedVideoFile, setCaptionedVideoFile] = useState<File | null>(null);
    const [captionedVideoUploadedFilename, setCaptionedVideoUploadedFilename] = useState<string | null>(null);
    const [captionStyle, setCaptionStyle] = useState<CaptionStyle>("clean");
    const [captionBrandColors, setCaptionBrandColors] = useState<BrandColors>(DEFAULT_BRAND_COLORS);
    const [captionColorIsCustom, setCaptionColorIsCustom] = useState<boolean>(false);
    const [showCaptionedVideoSubmitted, setShowCaptionedVideoSubmitted] = useState<boolean>(false);
    // MB-1461: opt-in pre-render caption review. When checked, submit does
    // NOT show the fire-and-forget modal — the user stays in the wizard:
    // waiting (transcription ~1-2 min) → transcript editor → explicit
    // Approve → the normal completion modal. Leaving mid-review warns once;
    // the server-side reaper deletes the held draft posts on abandonment.
    const [reviewCaptions, setReviewCaptions] = useState<boolean>(false);
    const [reviewWait, dispatchReviewWait] = useReducer(
        reviewWaitReducer,
        initialReviewWaitState
    );
    const [reviewTokens, setReviewTokens] = useState<CaptionToken[]>([]);
    const [reviewEditingId, setReviewEditingId] = useState<string | null>(null);
    const [reviewEditingValue, setReviewEditingValue] = useState<string>("");
    const [showReviewLeaveConfirm, setShowReviewLeaveConfirm] = useState<boolean>(false);
    const { socket, isAuthenticated: socketAuthenticated } = useSocket();
    const navigate = useNavigate();
    // State to track whether to show the confirmation overlay when disabling AI Enhance.
    const [showAiEnhanceConfirm, setShowAiEnhanceConfirm] =
        useState<boolean>(false);

    const [schoolDistrict, setSchoolDistrict] = useState<string>(
        listing?.schoolDistrict || ""
    );

    const [tags, setTags] = useState<string[]>([]);

    // We now use a fixed map for our four platforms.
    const defaultPlatformContent: PlatformContentMap = {
        facebook: { content: "" },
        instagram: { content: "" },
        twitter: { content: "" },
        linkedin: { content: "" },
        pinterest: { content: "" },
        google: { content: "" },
        youtube: { content: "" },
    };
    const [platformContent, setPlatformContent] = useState<PlatformContentMap>(
        defaultPlatformContent
    );

    // Media state (shared)
    const [selectedMedia, setSelectedMedia] = useState<string[]>([]);
    const [isCheckingVideoHash, setIsCheckingVideoHash] = useState(false);
    const [showDuplicateVideoAlert, setShowDuplicateVideoAlert] = useState(false);
    const [platformProfiles, setPlatformProfiles] = useState<Record<string, string>>({});

    const [media, setMedia] = useState<MediaCollection>({
        images: [],
        videos: [],
        assets: [],
    });
    const [isLoadingMedia, setIsLoadingMedia] = useState<boolean>(false);

    const user = useSelector((state: RootState) => state.auth.user);

    const isMobile = useIsMobile(900);
    const [loading, setLoading] = useState<boolean>(false);
    const [creating, setCreating] = useState<boolean>(false);
    const [customStatus, setCustomStatus] = useState<string>("");

    const [showCloseConfirm, setShowCloseConfirm] = useState<boolean>(false);
    const [openHouseDate, setOpenHouseDate] = useState<Date>(() => {
        const nextDate = new Date();
        nextDate.setDate(nextDate.getDate() + 1);
        return nextDate;
    });
    const [startTime, setStartTime] = useState({ hour: 10, minute: 0 });
    const [endTime, setEndTime] = useState({ hour: 12, minute: 0 });
    const [startPeriod, setStartPeriod] = useState<string>("AM");
    const [endPeriod, setEndPeriod] = useState<string>("PM");

    const [listings, setListings] = useState<ListingList[]>([]);
    const [selectedListing, setSelectedListing] = useState<ListingList>();
    // The ad-hoc picker entry carries the shared flag; the full listing the
    // media fetch loads is kept as a fallback for an api without it.
    const [selectedListingShared, setSelectedListingShared] = useState(false);

    const isLeadGen = selectedListing?.listingType?.toLowerCase().replace(/\s/g, '') === 'leadgeneration';
    const isMyBrand = selectedListing?.listingType?.toLowerCase().replace(/\s/g, '') === 'mybrand';
    const isTextOnly = selectedListing?.listingType?.toLowerCase().replace(/\s/g, '') === 'textonly';
    // My Niche Video: a My Brand captioned video, recorded with a
    // script. Exists only inside the captioned flow; AI Enhance is always on.
    const isNicheVideo = postCaptionedVideo && isNicheVideoListing(selectedListing?.listingType);
    // The audience selector only renders for these, so only these may
    // send one. Everything else would be transmitting a value the user cannot
    // see or change, seeded from whatever this browser last remembered.
    const audienceApplies = isLeadGen || isMyBrand || isNicheVideo;
    const isManualFlow = isAdHoc && !isLeadGen && !isNicheVideo && ((isMyBrand && !postCaptionedVideo) || isTextOnly || !aiEnhance);
    // Listing Post card: AI Enhance and the text box sit on the step after Platforms (the channel limits are
    // known there), so the flow always has a content step. Footers, limits and the manual flag still key on isManualFlow.
    const contentAfterPlatforms = preset?.card === "listing" && !postCaptionedVideo;
    const hasContentStep = isManualFlow || contentAfterPlatforms;
    // Step indexes for every flow (see wizardSteps): text only skips media;
    // captioned video ends on Schedule; My Niche Video adds Topic & script.
    const steps = wizardSteps({ isTextOnly, isManualFlow: hasContentStep, postCaptionedVideo, isNicheVideo });
    const totalSteps = steps.total;
    // Index of the final step. Used by the "creating" loading UI and CardTitle hide logic.
    const finalStepIndex = totalSteps - 1;
    const ownNiche = useNiche(isAdHoc && !pageNiche);
    const niche = pageNiche ?? ownNiche;
    const [nicheSelection, setNicheSelection] = useState<NicheSelection>({ topicId: null, script: "" });
    // Platforms whose schedule got the now + 15 pre-fill (see seedNewNicheSchedules).
    const nicheSeededPlatformsRef = useRef<ReadonlySet<string>>(new Set());
    // No saved niche: Basic Info holds the agent until My Niche is set up.
    const nicheNeedsSetup = isNicheVideo && !!niche.overview && !niche.overview.profile;
    // Topic & script has a request in flight (e.g. a Rewrite): Next waits for it.
    const [nicheStepBusy, setNicheStepBusy] = useState(false);


    const [generatePosts, setGeneratePosts] = useState<boolean>(false);
    const isGeneratingRef = useRef(false);
    const platformsWithApiFooter = useRef<Set<string>>(new Set());
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pollCountRef = useRef(0);
    const [apiFooterFallback, setApiFooterFallback] = useState<string>("");



    const [availableFooter, setAvailableFooter] = useState<string>("");
    const [availableGoogleFooter, setAvailableGoogleFooter] =
        useState<string>("");
    const [shouldSyncParentState, setShouldSyncParentState] = useState(false);
    const [platformGraphics, setPlatformGraphics] = useState<PlatformGraphicsMap>({
        facebook: { bgColor: { type: 'solid', color: '#000000' }, textColor: '#FFFFFF', fontSize: 24, fontFamily: 'Roboto' },
        instagram: { bgColor: { type: 'solid', color: '#000000' }, textColor: '#FFFFFF', fontSize: 24, fontFamily: 'Roboto' },
        twitter: { bgColor: { type: 'solid', color: '#000000' }, textColor: '#FFFFFF', fontSize: 24, fontFamily: 'Roboto' },
        linkedin: { bgColor: { type: 'solid', color: '#000000' }, textColor: '#FFFFFF', fontSize: 24, fontFamily: 'Roboto' },
        pinterest: { bgColor: { type: 'solid', color: '#000000' }, textColor: '#FFFFFF', fontSize: 24, fontFamily: 'Roboto' },
        google: { bgColor: { type: 'solid', color: '#000000' }, textColor: '#FFFFFF', fontSize: 24, fontFamily: 'Roboto' },
    });
    const [areTokensFilled, setAreTokensFilled] = useState<boolean>(true);
    
    // Voice family for this piece. Seeded from the last choice THIS user made
    // so the wizard opens where they left off, then written back on every
    // change. The choice itself stays per piece — one account can publish a
    // commercial piece and a residential one the same day.
    //
    // state.auth.user is the effective user (impersonation overwrites it), so
    // the seed follows whoever is actually acting.
    const effectiveUserId = useAppSelector((state) => state.auth.user?._id);
    const audiencePref = useMemo(
        () => contentAudiencePref(effectiveUserId),
        [effectiveUserId]
    );
    const [contentAudience, setContentAudience] = useState<ContentAudience>(() =>
        audiencePref.get()
    );
    // Re-seed when the acting user changes, so an impersonation switch loads
    // that account's own choice instead of keeping the previous one on screen.
    useEffect(() => {
        setContentAudience(audiencePref.get());
    }, [audiencePref]);
    const handleContentAudienceChange = useCallback(
        (value: ContentAudience) => {
            if (value === contentAudience) return;
            setContentAudience(value);
            audiencePref.set(value);
            // A niche script is written in one voice: a topic picked in the old voice starts over.
            if (isNicheVideo && nicheSelection.topicId) {
                setNicheSelection({ topicId: null, script: "" });
                setCaptionedVideoFile(null);
                setCaptionedVideoUploadedFilename(null);
            }
        },
        [audiencePref, contentAudience, isNicheVideo, nicheSelection.topicId]
    );

    // Where a captioned video's captions sit: Bottom whenever the wizard opens;
    // the pick then holds while it is open, like captionStyle above.
    const { captionPosition, setCaptionPosition } = useCaptionPosition(effectiveUserId);

    // The recorder's caption band and face guide: on unless this user turned
    // them off, re-seeded and written back per effective user like the above.
    const guidesPrefForUser = useMemo(
        () => recorderGuidesPref(effectiveUserId),
        [effectiveUserId]
    );
    const [guidesOn, setGuidesOn] = useState(() => guidesPrefForUser.get() !== "off");
    useEffect(() => {
        setGuidesOn(guidesPrefForUser.get() !== "off");
    }, [guidesPrefForUser]);
    const handleGuidesToggle = useCallback(() => {
        const next = !guidesOn;
        setGuidesOn(next);
        guidesPrefForUser.set(next ? "on" : "off");
    }, [guidesOn, guidesPrefForUser]);

    // Lead Generation state persistence
    const [leadGenerationType, setLeadGenerationType] = useState<string>("");
    const [leadGenerationSubtypes, setLeadGenerationSubtypes] = useState<string[]>([]);
    const [leadGenerationPrompts, setLeadGenerationPrompts] = useState<string[]>([]);
    const [selectedPrompt, setSelectedPrompt] = useState<string>("");
    const [promptParts, setPromptParts] = useState<Array<{ type: 'text' | 'token'; value: string; key?: string }>>([]);
    // Add new state to track if we should show the ReconfirmOverlay for step-back
    const [showStepBackConfirm, setShowStepBackConfirm] = useState(false);
    // Confirm dialog when user tries to go back from last step with AI enhance active
    const [showAiRegenConfirm, setShowAiRegenConfirm] = useState(false);
    // State to confirm clearing media when navigating back from media selection step
    const [showClearMediaConfirm, setShowClearMediaConfirm] = useState(false);
    // Track whether content was pasted and truncated without subsequent user edits
    const [wasPastedAndTruncated, setWasPastedAndTruncated] = useState(false);
    // Show confirmation before continuing with truncated pasted content
    const [showTruncationConfirm, setShowTruncationConfirm] = useState(false);
    // Ref to suppress handlePrevious when Radix Dialog fires onOpenChange on unmount during forward navigation
    const isNavigatingForwardRef = useRef(false);
    // That close can only fire while the next step commits; clear the guard after it so it never
    // swallows the user's next Back click.
    useEffect(() => {
        if (!isNavigatingForwardRef.current) return;
        const id = window.setTimeout(() => { isNavigatingForwardRef.current = false; }, 0);
        return () => window.clearTimeout(id);
    }, [currentStep]);
    const [youtubeProfileUrl, setYoutubeProfileUrl] = useState<string>("");
    const [isYouTubeShort, setIsYouTubeShort] = useState<boolean | null>(null);

     // Reset rememberedDefaultSchedule when selectedListing changes
    useEffect(() => {
        setRememberedDefaultSchedule(null);
        // Reset selected media when listing changes
    setSelectedMedia([]);
    }, [selectedListing]);

    // Detect YouTube video aspect ratio early (at step 3) so step 4 renders the correct component immediately
    useEffect(() => {
        const isYouTubeSelected = selectedPlatforms.some(
            (p) => p.id.toLowerCase() === "youtube"
        );
        if (currentStep !== 3 || !isYouTubeSelected) return;

        const allVideos = [
            ...(media.videos ?? []),
            ...(media.assets ?? []).filter((a) => (a as any).assetType === "Video"),
        ];
        const selectedVideo = allVideos.find((v) => selectedMedia.includes(v.id));
        const videoUrl = selectedVideo?.signedUrl;

        if (!videoUrl) {
            setIsYouTubeShort(null);
            return;
        }

        // Use server-provided aspect ratio if available (avoids async detection on mobile)
        const storedAspectRatio = (selectedVideo as any)?.videoMetadata?.aspectRatio;
        if (storedAspectRatio) {
            const parts = storedAspectRatio.split(":");
            if (parts.length === 2) {
                const w = parseFloat(parts[0]);
                const h = parseFloat(parts[1]);
                if (w && h) {
                    setIsYouTubeShort(w / h <= 0.8);
                    return;
                }
            }
        }

        // Fallback: detect via async video metadata load
        setIsYouTubeShort(null);

        const vid = document.createElement("video");
        vid.preload = "metadata";
        vid.src = videoUrl;
        vid.onloadedmetadata = () => {
            const ratio = vid.videoWidth / vid.videoHeight;
            setIsYouTubeShort(ratio <= 0.8);
            vid.src = "";
        };
        vid.onerror = () => {
            setIsYouTubeShort(false);
            vid.src = "";
        };

        return () => {
            vid.src = "";
        };
    }, [currentStep, selectedPlatforms, selectedMedia, media]);

    // Apply YouTube content length default based on detected aspect ratio,
    // but only when AI enhance is on and the user has opted out of default settings.
    useEffect(() => {
        if (isYouTubeShort === null) return;
        if (!aiEnhance) return;
        if (defaultSettings) return;
        setPlatformSchedules((prev) => {
            if (!prev.youtube) return prev;
            return {
                ...prev,
                youtube: {
                    ...prev.youtube,
                    contentLength: isYouTubeShort ? ContentLength.Short : ContentLength.Medium,
                },
            };
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isYouTubeShort, aiEnhance, defaultSettings]);

    // Reset Lead Generation state when dialog closes
    useEffect(() => {
        if (!open) {
            setLeadGenerationType("");
            setLeadGenerationSubtypes([]);
            setLeadGenerationPrompts([]);
            setSelectedPrompt("");
            setPromptParts([]);
        }
    }, [open]);

    // The created-platforms memory only makes sense while the same post is
    // being retried: a different listing, or a different post type (Text Only
    // shares the My Brand entry's id), is a different post.
    useEffect(() => {
        createdAdhocPlatformsRef.current.clear();
    }, [selectedListing?.id, selectedListing?.listingType]);

    useEffect(() => {
        // get plan post limit
        const fetchPlanPostLimit = async () => {
            try {
                const headers = await getAuthHeader();
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/usage/post-limit`,
                    { headers }
                );
                setPlanLimit(response.data);
            } catch (error) {
                console.error("Error fetching plan limit:", error);
            }
        };
        fetchPlanPostLimit();
    }, []);

    useEffect(() => {
        if (listing?.parsedFooter) {
            setAvailableFooter(listing.parsedFooter);
        } else if (selectedListing?.parsedFooter) {
            setAvailableFooter(selectedListing.parsedFooter);
        }

        if (listing?.parsedGoogleFooter) {
            setAvailableGoogleFooter(listing.parsedGoogleFooter);
        } else if (selectedListing?.parsedGoogleFooter) {
            setAvailableGoogleFooter(selectedListing?.parsedGoogleFooter);
        }
    }, [listing, selectedListing]);

    // When user clicks No on "Use default settings?", populate footer for platforms
    // that didn't receive footer data from the API. Re-fires if footer source arrives late.
    useEffect(() => {
        if (!defaultSettings) {
            const footer = availableFooter || apiFooterFallback;
            if (footer) {
                setPlatformSchedules((prev) => {
                    const updated = { ...prev };
                    selectedPlatforms.forEach(({ id }) => {
                        const key = id.toLowerCase();
                        if (key === 'google' || key === 'instagram') return;
                        if (platformsWithApiFooter.current.has(key)) return;
                        if (!updated[key]) return;
                        if (updated[key].footer) return; // already populated, don't overwrite
                        updated[key] = {
                            ...updated[key],
                            footer,
                            includeFooter: true,
                            footerType: (key === 'pinterest' || key === 'google') ? 'in-post' : updated[key].footerType,
                        };
                    });
                    return updated;
                });
            }
        }
    }, [defaultSettings, availableFooter, apiFooterFallback, selectedPlatforms]);

    // Helper function to initialize platform schedules only once
    const initializePlatformSchedules = () => {
        
        const schedules: Record<string, InitialSchedule> = {
            facebook: makeInitialSchedule("facebook", ""),
            instagram: makeInitialSchedule("instagram", ""),
            twitter: makeInitialSchedule("twitter", ""),
            linkedin: makeInitialSchedule("linkedin", ""),
            pinterest: makeInitialSchedule("pinterest", ""),
            google: makeInitialSchedule("google", ""),
        };
        
        return schedules;
    };

    const [platformSchedules, setPlatformSchedules] = useState<
        Record<string, InitialSchedule>
    >(() => {
        const initialSchedules = initializePlatformSchedules();
        return initialSchedules;
    });

    // Prevent re-initialization on re-renders
    const hasInitialized = useRef(false);
    // Track whether API contentLength defaults have been applied once (to avoid overwriting user's selection on step revisit)
    const hasAppliedContentLengthDefaults = useRef(false);
    // Track whether API footerType defaults have been applied once
    const hasAppliedFooterTypeDefaults = useRef(false);
    // Track whether API includeFooter defaults have been applied once (to avoid re-enabling footer after user toggled it off)
    const hasAppliedIncludeFooterDefaults = useRef(false);
    // Snapshot of last generation payload — skip API if inputs haven't changed
    const generationSnapshotRef = useRef<string | null>(null);
    // Snapshot of MyBrand emojis/usePrice to detect setting changes in step 3
    const myBrandSettingsSnapshotRef = useRef<string | null>(null);
    const [shouldTriggerMyBrandGen, setShouldTriggerMyBrandGen] = useState(false);
    
    useEffect(() => {
        // Only initialize if we haven't done it before and schedules are empty
        if (!hasInitialized.current) {
            const hasValidSchedules = Object.keys(platformSchedules).length > 0 && 
                                     Object.values(platformSchedules).every(schedule => schedule && schedule.date);
            
            if (!hasValidSchedules) {
                setPlatformSchedules(initializePlatformSchedules());
            }
            hasInitialized.current = true;
        }
         // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array - only run once

    useEffect(() => {
        setSchoolDistrict(listing?.schoolDistrict || "");
    }, [listing]);

    // Reset post content when switching between different listing types
    const previousListingTypeRef = useRef<string | undefined>();
    
    useEffect(() => {
        if (previousListingTypeRef.current && selectedListing?.listingType) {
            const prevType = previousListingTypeRef.current.toLowerCase().replace(/\s/g, '');
            const currentType = selectedListing.listingType.toLowerCase().replace(/\s/g, '');
            
            // Only reset when switching TO a different listing type, not when navigating back to same type
            if (prevType !== currentType) {
                setGeneralInstructions('');
                setPlatformContent({
                    facebook: { content: '', title: '' },
                    instagram: { content: '', title: '' },
                    twitter: { content: '', title: '' },
                    linkedin: { content: '', title: '' },
                    pinterest: { content: '', title: '' },
                    google: { content: '', title: '' },
                    youtube: { content: '', title: '' },
                });
                // Reset platform graphics to default values
                setPlatformGraphics({
                    facebook: { bgColor: { type: 'solid', color: '#000000' }, textColor: '#FFFFFF', fontSize: 24, fontFamily: 'Roboto' },
                    instagram: { bgColor: { type: 'solid', color: '#000000' }, textColor: '#FFFFFF', fontSize: 24, fontFamily: 'Roboto' },
                    twitter: { bgColor: { type: 'solid', color: '#000000' }, textColor: '#FFFFFF', fontSize: 24, fontFamily: 'Roboto' },
                    linkedin: { bgColor: { type: 'solid', color: '#000000' }, textColor: '#FFFFFF', fontSize: 24, fontFamily: 'Roboto' },
                    pinterest: { bgColor: { type: 'solid', color: '#000000' }, textColor: '#FFFFFF', fontSize: 24, fontFamily: 'Roboto' },
                    google: { bgColor: { type: 'solid', color: '#000000' }, textColor: '#FFFFFF', fontSize: 24, fontFamily: 'Roboto' },
                });
                // Reset selected platforms since different listing types have different platform availability
                setChosenPlatforms({
                    facebook: false,
                    instagram: false,
                    twitter: false,
                    linkedin: false,
                    pinterest: false,
                    google: false,
                    youtube: false,
                });
                // Reset media type so platform filtering starts fresh for the new listing type
                setSelectedMediaType(isVideo ? "video" : "");
                // Reset to step 0 (Listing Selection) to allow proper workflow restart
                setCurrentStep(0);
            }
        }
        
        previousListingTypeRef.current = selectedListing?.listingType;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedListing?.listingType, isVideo, setPlatformContent, setPlatformGraphics]);

    // My Niche Video is record-only, so a take never carries into or out of it, including
    // via unticking captioned, which clears the listing and so skips the reset above.
    const wasNicheVideoRef = useRef(isNicheVideo);
    useEffect(() => {
        if (wasNicheVideoRef.current === isNicheVideo) return;
        wasNicheVideoRef.current = isNicheVideo;
        setCaptionedVideoFile(null);
        setCaptionedVideoUploadedFilename(null);
    }, [isNicheVideo]);

    // Handler to intercept changes to the aiEnhance flag.
    const handleAiEnhanceChange = (newValue: boolean) => {
        // If AI enhance is currently enabled and the user wants to disable it...
        if (aiEnhance && !newValue) {
            setShowAiEnhanceConfirm(true);
        } else {
            // Otherwise update immediately (for enabling or no change)
            setAiEnhance(newValue);
        }
    };

    // Callback for when the user confirms the disable action.
    const confirmAiEnhanceDisable = (): boolean => {
        setAiEnhance(false);
        setShowAiEnhanceConfirm(false);
        // Reset so the content population effect re-runs from generalInstructions
        // when the user reaches the review step in manual flow
        setShouldSyncParentState(false);
        setGeneratePosts(false);
        setPlatformContent({
            facebook: { content: "" },
            instagram: { content: "" },
            twitter: { content: "" },
            linkedin: { content: "" },
            pinterest: { content: "" },
            google: { content: "" },
            youtube : { content: "" },
        });
        // Clear AI-generated footer from schedules so it doesn't show in the preview
        setPlatformSchedules(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(key => {
                updated[key] = {
                    ...updated[key],
                    footer: "",
                    includeFooter: false,
                };
            });
            return updated;
        });
        return true;
    };

    // Callback for when the user cancels the disable action.
    const cancelAiEnhanceDisable = () => {
        setShowAiEnhanceConfirm(false);
    };

    // Fetch user platforms from backend (optional)
    useEffect(() => {
        const fetchPlatformsData = async () => {
            try {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;

                // Fetch user's social profiles
                const userResponse = await axios.get(
                    `${backendUrl}/socials/profiles`,
                    { headers }
                );
                if (userResponse.status !== 200) {
                    throw new Error("Failed to fetch social profiles.");
                }
                const userData = userResponse.data.data || userResponse.data;
                const activeAccounts: string[] = userData.activeSocialAccounts;

                // Map the filtered accounts to Platform objects using PLATFORM_LIMITS.
                const platforms: Platform[] = activeAccounts.map((account) => {
                    const key = account.toLowerCase();
                    const config = PLATFORM_LIMITS[key];
                    if (config) {
                        const idMap: Record<string, SocialPlatforms> = {
                            facebook:  SocialPlatforms.Facebook,
                            instagram: SocialPlatforms.Instagram,
                            twitter:   SocialPlatforms.Twitter,
                            linkedin:  SocialPlatforms.LinkedIn,
                            pinterest: SocialPlatforms.Pinterest,
                            google:    SocialPlatforms.Google,
                            gmb:       SocialPlatforms.Google,
                            youtube:   SocialPlatforms.YouTube,
                        };
                        return {
                            id: idMap[key] ?? (account as SocialPlatforms),
                            name: config.label,
                            characterLimit: config.limit,
                            requiresMedia: config.requiresMedia,
                            maxImages: config.maxImages,
                            maxVideos: config.maxVideos,
                        };
                    }
                    return {
                        id: account as SocialPlatforms,
                        name: account,
                        characterLimit: 1000,
                        requiresMedia: false,
                        maxImages: 5,
                        maxVideos: 1,
                    };
                });

                setAvailablePlatforms(platforms);
                setSelectedPlatforms(platforms);
            } catch (error) {
                console.error("Error fetching social profiles: ", error);
            }
        };

        fetchPlatformsData();
    }, [campaignPlatforms]);

    // Fetch existing schedule data when step 2
    useEffect(() => {
        if (isAdHoc && currentStep !== 3) return;
        if ((isVideo || isOpenHouse) && currentStep !== 2) return;
        // Skip for text only — no real listing campaign to fetch
        if (isTextOnly) return;
        (async () => {
            try {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const resp = await axios.post(
                    `${backendUrl}/listing/campaign-platforms`,
                    {
                        listingId: selectedListing?.id || listing?.id || "",
                    },
                    { headers }
                );

                if (resp.status === 200) {
                    // Track which platforms received a non-empty footer from the API
                    const apiData = resp.data as { name: string; footer?: string }[];
                    platformsWithApiFooter.current = new Set(
                        apiData.filter((pl) => !!pl.footer).map((pl) => pl.name.toLowerCase())
                    );
                    // Store first available footer from API as fallback for platforms not in response
                    setApiFooterFallback(apiData.find((pl) => !!pl.footer)?.footer ?? "");

                    setPlatformSchedules((prev) => {
                        const updated = { ...prev };
                        
                        // ✅ FIX: Only create new schedules for platforms that don't exist
                        // Don't reset existing schedules that user has already configured
                        for (const key in updated) {
                            if (!updated[key]) { // Only if schedule doesn't exist
                                updated[key] = makeInitialSchedule(
                                    key,
                                    key === "google"
                                        ? availableGoogleFooter
                                        : key === "instagram"
                                        ? ""
                                        : availableFooter
                                );
                            }
                            // If schedule exists, preserve it (don't reset to current time)
                        }

                        (resp.data as any[]).forEach((pl) => {
                            const key = pl.name.toLowerCase();
                            console.debug(
                                `Updating platform '${key}' with data: `,
                                pl
                            );
                            if (!updated[key] || !pl.postTimes?.length) return;
                            
                            const { hour: h24, minute: m } = pl.postTimes[0];
                            const period = h24 >= 12 ? "PM" : "AM";
                            let h12 = h24 % 12;
                            if (!h12) h12 = 12;
                            
                            // ✅ PRESERVE USER'S SELECTED DATE - don't reset to current date
                            let dt = new Date(updated[key].date); // Use existing date
                            dt.setHours(
                                period === "PM"
                                    ? h12 + 12 - (h12 === 12 ? 12 : 0)
                                    : h12 % 12,
                                m
                            );
                            
                            // A campaign time already past on the chosen day moves to the next day it is still ahead,
                            // judged in the profile time zone like the Continue check. The default date is today, so a
                            // morning campaign time used to stay in the past.
                            dt = rollToFuture(dt, { hour: h12, minute: m }, period, user?.timeZone || "");

                            // My Niche Video keeps its now + 15 pre-fill; the campaign's
                            // footer and content defaults still apply.
                            const time = isNicheVideo ? {} : { date: dt, hour: h12, minute: m, period };
                            updated[key] = {
                                ...updated[key],
                                ...time,
                                contentLength:
                                    (!hasAppliedContentLengthDefaults.current && pl.contentLengths?.[0]) ||
                                    updated[key].contentLength,
                                footer: pl.footer || updated[key].footer,
                                includeFooter:
                                    (!hasAppliedIncludeFooterDefaults.current && pl.includeFooter !== undefined)
                                        ? pl.includeFooter
                                        : updated[key].includeFooter,
                                footerType: (!hasAppliedFooterTypeDefaults.current && pl.footerType)
                                    ? pl.footerType
                                    : updated[key].footerType,
                                threads:
                                    pl.threads !== undefined
                                        ? pl.threads
                                        : updated[key].threads,
                            };
                        });

                        return updated;
                    });
                    hasAppliedContentLengthDefaults.current = true;
                    hasAppliedFooterTypeDefaults.current = true;
                    hasAppliedIncludeFooterDefaults.current = true;
                }
            } catch (e) {
                toast.error(
                    <CustomToast
                        message="Failed to fetch campaign platforms."
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
            }
        })();
    }, [
        currentStep,
        listing,
        selectedListing,
        isVideo,
        isOpenHouse,
        isAdHoc,
        isTextOnly,
        isNicheVideo,
        availableFooter,
        availableGoogleFooter,
        user?.timeZone,
    ]);

    // Sync schedule and content objects when chosen platforms change
    useEffect(() => {  
        // An unchosen platform loses its schedule; if it comes back, its fresh one needs the niche pre-fill.
        nicheSeededPlatformsRef.current = forgetUnchosenSeeded(
            nicheSeededPlatformsRef.current,
            (key) => !!chosenPlatforms[key as keyof SocialsConnected],
        );
        setPlatformSchedules((prev) =>
            Object.keys(chosenPlatforms).reduce((acc, key) => {
                if (chosenPlatforms[key as keyof SocialsConnected]) {
                    // Always preserve existing schedule if it exists, never reset to current time
                    acc[key] = prev[key] || makeInitialSchedule(key, availableFooter);
                }
                return acc;
            }, {} as Record<string, InitialSchedule>)
        );
        setPlatformContent((prev) =>
            Object.keys(chosenPlatforms).reduce((acc, key) => {
                if (chosenPlatforms[key as keyof SocialsConnected]) {
                    acc[key as keyof PlatformContentMap] = prev[
                        key as keyof PlatformContentMap
                    ] || { content: "" };
                }
                return acc;
            }, {} as PlatformContentMap)
        );
    }, [chosenPlatforms, availableFooter]);

    // Fetch listing media and assets
    const fetchListingMedia = useCallback(async () => {
        setSelectedListingShared(false);
        const isMyBrand = selectedListing?.listingType.toLowerCase().replace(/\s/g, '') === "mybrand";
        const isLeadGeneration = selectedListing?.listingType.toLowerCase().replace(/\s/g, '') === "leadgeneration";
        const isTextOnlyListing = selectedListing?.listingType.toLowerCase().replace(/\s/g, '') === "textonly";
        const isNicheVideoType = isNicheVideoListing(selectedListing?.listingType);
        // 🚫 Do not fetch media for My Brand, Lead Generation, Text Only or My Niche Video
        if (isMyBrand || isLeadGeneration || isTextOnlyListing || isNicheVideoType) return;
        if (!listing && !selectedListing) return;

        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        try {
            const response = await axios.get(
                `${backendUrl}/listing/${
                    selectedListing?.id ||
                    listing?.id ||
                    listing?._id?.toString()
                }`,
                { headers }
            );
            if (response.status === 200) {
                setSelectedListingShared(isSharedListing(response.data));
                const images = isVideo ? undefined : response.data.images;
                const rawVideos = response.data.videos ?? [];
                const nestedVideos: any[] = response.data.campaign?.listingId?.videos ?? [];
                const hashById = new Map(nestedVideos.map((v: any) => [v.id, v.videoHash]));
                const videos = rawVideos.map((v: any) => ({
                    ...v,
                    videoHash: hashById.get(v.id) ?? v.videoHash,
                }));

                setMedia((prevMedia) => ({
                    ...prevMedia,
                    images,
                    videos,
                }));
            } else {
                throw new Error(
                    "Failed to fetch listing media. Please try again later."
                );
            }
        } catch (error) {
            console.error("Failed to fetch listing media:", error);
        }
    }, [listing, isVideo, selectedListing]);

    const fetchResources = useCallback(async () => {
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.get(
                `${backendUrl}/assets/general-assets`,
                { headers }
            );

            const videoAssets = response.data.filter(
                (asset: Assets) => asset.assetType === "Video"
            );

            if (response.status === 200) {
                setMedia((prevMedia) => ({
                    ...prevMedia,
                    assets: isVideo ? videoAssets : response.data,
                }));
            } else {
                throw new Error(
                    "Failed to fetch resources. Please try again later."
                );
            }
        } catch (error) {
            console.error("Failed to fetch resources:", error);
        }
    }, [isVideo]);

    useEffect(() => {
        const loadMedia = async () => {
            setIsLoadingMedia(true);
            try {
                await fetchListingMedia();
                await fetchResources();
            } finally {
                setIsLoadingMedia(false);
            }
        };
        loadMedia();
    }, [fetchListingMedia, fetchResources]);

    // Refresh assets after the captioned-video upload pipeline completes so that
    // handleCreatePost can resolve the real backend asset id/url from media.assets.
    useEffect(() => {
        if (captionedVideoUploadedFilename) {
            fetchResources();
        }
    }, [captionedVideoUploadedFilename, fetchResources]); // eslint-disable-line react-hooks/exhaustive-deps

    // Silent refresh for background updates (e.g., after upload completion)
    const fetchMediaSilent = useCallback(async () => {
        try {
            await fetchListingMedia();
            await fetchResources();
        } catch (error) {
            console.error("Error in silent media fetch:", error);
        }
    }, [fetchListingMedia, fetchResources]);

    // Targeted poll for a single asset by ID — avoids fetching the entire ever-growing asset list
    const pollAssetById = useCallback(async (id: string) => {
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.get(
                `${backendUrl}/assets/general-assets/${id}`,
                { headers }
            );
            if (response.status === 200) {
                const updatedAsset = response.data as MediaItem;
                setMedia((prev) => ({
                    ...prev,
                    assets: (prev.assets || []).map((a) =>
                        a.id === id ? updatedAsset : a
                    ),
                }));
            }
        } catch (error) {
            console.error("Failed to poll asset status:", error);
        }
    }, []);

    // Detect if any currently-selected asset/video is still being converted by the backend
    const hasProcessingSelectedMedia = useMemo(() => {
        const allAssets = [...(media.assets || []), ...(media.videos || [])];
        return selectedMedia.some((id) => {
            const asset = allAssets.find((a) => a.id === id);
            return asset?.isProcessing === true;
        });
    }, [selectedMedia, media.assets, media.videos]);

    // Review step index: see wizardSteps
    const isOnReviewStep = currentStep === steps.review;

    // Poll GET /assets/general-assets/:id every 5s while on the review step with a processing video.
    // When isProcessing flips to false, pollAssetById patches media.assets, hasProcessingSelectedMedia
    // recomputes to false, and this effect self-clears the interval.
    useEffect(() => {
        const POLL_INTERVAL = 5000;
        const MAX_POLLS = 12; // 1 minute

        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            pollCountRef.current = 0;
        }

        if (!isOnReviewStep || !hasProcessingSelectedMedia) return;

        // Snapshot the IDs that are processing right now — these are fixed for this interval's lifetime
        const allAssets = [...(media.assets || []), ...(media.videos || [])];
        const processingIds = selectedMedia.filter((id) => {
            const asset = allAssets.find((a) => a.id === id);
            return asset?.isProcessing === true;
        });

        pollCountRef.current = 0;
        pollIntervalRef.current = setInterval(async () => {
            pollCountRef.current++;
            await Promise.all(processingIds.map((id) => pollAssetById(id)));

            if (pollCountRef.current >= MAX_POLLS) {
                clearInterval(pollIntervalRef.current!);
                pollIntervalRef.current = null;
                toast.info(
                    <CustomToast
                        message="Video is still processing. You can feel free to schedule your post."
                        type="info"
                    />,
                    { autoClose: 6000 }
                );
            }
        }, POLL_INTERVAL);

        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, [isOnReviewStep, hasProcessingSelectedMedia, pollAssetById, selectedMedia, media.assets, media.videos]);

    const convertMediaCollection = (
        collection: MediaCollection
    ): PostRelatedMedia => {
        return {
            images: collection.images?.map((img) => ({
                ...img,
                tag: (img as any).tag || "",
            })),
            videos: collection.videos,
            assets: collection.assets?.map((asset) => ({
                ...asset,
                assetType: asset.assetType
                    ? (asset.assetType as AssetType)
                    : "Image",
            })),
        };
    };

    const hasScheduleAfterOpenHouse = (): boolean => {
        if (!isOpenHouse) return false;

        for (const platform of selectedPlatforms) {
            const key = platform.id.toLowerCase();
            const sched = platformSchedules[key];
            const ts = getScheduledDateTime(
                sched.date,
                { hour: sched.hour, minute: sched.minute },
                sched.period,
                user?.timeZone || ""
            );
            if (!ts) continue; // already caught by hasInvalidSchedule
            const when = new Date(ts);
            // if your openHouseDate is at midnight, you may want to compare only dates:
            if (when > openHouseDate) {
                console.debug(
                    `${key}: scheduled ${when.toISOString()} is after open house ${openHouseDate.toISOString()}`
                );
                return true;
            }
        }
        return false;
    };

    // Check whether the selected video's hash has already been published to YouTube.
    // Returns true if the hash is already used (caller should block navigation), false otherwise.
    const checkYoutubeVideoHash = async (): Promise<boolean> => {
        if (!chosenPlatforms.youtube) return false;

        const allVideos = [
            ...(media.videos ?? []),
            ...(media.assets ?? []).filter((a) => a.assetType === "Video"),
        ];
        const selectedVideo = allVideos.find((v) => selectedMedia.includes(v.id));
        const videoHash = selectedVideo?.videoHash;

        if (!videoHash) return false;

        setIsCheckingVideoHash(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const { data } = await axios.post(
                `${backendUrl}/post/youtube/check-video-hash`,
                { videoHashes: [videoHash] },
                { headers }
            );
            if (data?.available === false) {
                setShowDuplicateVideoAlert(true);
                return true;
            }
        } catch {
            // API failure — don't block the user
        } finally {
            setIsCheckingVideoHash(false);
        }
        return false;
    };

    // Navigation handlers
    const handleNext = async () => {
        // My Niche Video: Topic & script → Record → Schedule.
        if (isNicheVideo && currentStep === steps.topic) {
            if (nicheStepBusy) return;
            if (!nicheSelection.topicId || !nicheSelection.script.trim()) {
                toast.error(
                    <CustomToast message="Pick a topic and keep a script to read." type="error" />,
                    { autoClose: 3000 }
                );
                return;
            }
            setPreviousStep(currentStep);
            setCurrentStep(steps.media);
            return;
        }
        if (isNicheVideo && currentStep === steps.media) {
            if (await checkYoutubeVideoHash()) return;
            // Pre-fill now + 15 on each platform's first arrival (spec §4.6); Complete bumps a
            // time that has passed by then. The updater reads a snapshot of the ref, so
            // running it twice gives the same result.
            const seededBefore = nicheSeededPlatformsRef.current;
            const now = new Date();
            setPlatformSchedules((prev) => {
                const next = seedNewNicheSchedules(prev, seededBefore, now, user?.timeZone || "");
                nicheSeededPlatformsRef.current = next.seeded;
                return next.schedules;
            });
            setPreviousStep(currentStep);
            setCurrentStep(steps.schedule);
            return;
        }
        if (currentStep === 0) {
            if (nicheNeedsSetup) {
                toast.error(
                    <CustomToast message="Set up My Niche first." type="error" />,
                    { autoClose: 3000 }
                );
                return;
            }
            if (isVideo) {
                // Validate video post type selection
                if (!videoPostType) {
                    toast.error(
                        <CustomToast
                            message="Please select a video showcase post type."
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                    return;
                }

                if (
                    videoPostType === VideoPostType.Feature &&
                    tags.length <= 0
                ) {
                    toast.error(
                        <CustomToast
                            message="Please enter at least one tag."
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                    return;
                }
            }

            if (isAdHoc) {
                if (!selectedListing) {
                    toast.error(
                        <CustomToast
                            message="Please select a listing to continue"
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                    return;
                }
            }

            // A flow with a content step validates the text there, not here
            if (!hasContentStep) {
                if (isAdHoc && (generalInstructions.includes('[') || generalInstructions.includes(']'))) {
                    toast.error(
                        <CustomToast
                            message="Please fill value / replace value in brackets."
                            type="error"
                        />, { autoClose: 3000 }
                    );
                    return;
                }

                if (
                    isAdHoc &&
                    !postCaptionedVideo &&
                    generalInstructions.trim().length === 0
                ) {
                    toast.error(
                        <CustomToast
                            message="Please enter your post content."
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                    return;
                }

                if (
                    (isOpenHouse || isVideo) &&
                    !aiEnhance &&
                    generalInstructions.trim().length === 0
                ) {
                    toast.error(
                        <CustomToast
                            message="Please enter your post content."
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                    return;
                }

                if (
                    (selectedListing?.listingType.toLowerCase().replace(/\s/g, '') === "leadgeneration" && generalInstructions.length > 200) ||
                    (selectedListing?.listingType.toLowerCase().replace(/\s/g, '') !== "leadgeneration" && aiEnhance && generalInstructions.length > 500) ||
                    (selectedListing?.listingType.toLowerCase().replace(/\s/g, '') !== "leadgeneration" && !aiEnhance && generalInstructions.length > 4200)
                ) {
                    toast.error(
                        <CustomToast
                            message={`Post content exceeds maximum length (${selectedListing?.listingType.toLowerCase().replace(/\s/g, '') === "leadgeneration" ? 200 : (aiEnhance ? 500 : 4200)} characters). Please shorten it.`}
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                    return;
                }
            }

            setPreviousStep(currentStep);
            setCurrentStep(1);
        } else if (currentStep === 1) {
            // Platform selection step (step 1 in platforms-first flow)
            if (
                !Object.values(chosenPlatforms).some((isSelected) => isSelected)
            ) {
                toast.error(
                    <CustomToast
                        message="Please select at least one platform."
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
                return;
            }
            if (
                planLimit <
                Object.values(chosenPlatforms).filter(
                    (isSelected) => isSelected
                ).length
            ) {
                setPlanLimitType("postSelection");
                setPlanLimitMessage(undefined);
                setPlanLimitReached(true);
                return;
            }

            setPreviousStep(currentStep);
            setCurrentStep(2);
        } else if (currentStep === 2) {
            if (hasContentStep) {
                // Content step: content entry validation
                if (generalInstructions.includes('[') || generalInstructions.includes(']')) {
                    toast.error(
                        <CustomToast
                            message="Please fill value / replace value in brackets."
                            type="error"
                        />, { autoClose: 3000 }
                    );
                    return;
                }
                if (generalInstructions.trim().length === 0) {
                    toast.error(
                        <CustomToast
                            message="Please enter your post content."
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                    return;
                }
                if (contentAfterPlatforms && aiEnhance && generalInstructions.length > 500) {
                    toast.error(
                        <CustomToast
                            message="AI instructions exceed the maximum length (500 characters). Please shorten them."
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                    return;
                }
                // If content was pasted and truncated without user edits, confirm before proceeding
                if (wasPastedAndTruncated) {
                    setShowTruncationConfirm(true);
                    return;
                }
                setPreviousStep(currentStep);
                // Text only skips media step — go straight to schedule (step 3)
                setCurrentStep(3);
            } else {
                // Non-manual flow step 2: media selection
                if (
                    chosenPlatforms.instagram ||
                    chosenPlatforms.pinterest ||
                    chosenPlatforms.google ||
                    isVideo
                ) {
                    // Skip media validation for lead generation listing type
                    // and for the captioned-video flow (which uses captionedVideoFile, not selectedMedia)
                    if (selectedListing?.listingType.toLowerCase().replace(/\s/g, '') !== "leadgeneration") {
                        if (!postCaptionedVideo && selectedMedia.length === 0) {
                            toast.error(
                                <CustomToast
                                    message="Please select media for posts."
                                    type="error"
                                />,
                                { autoClose: 3000 }
                            );
                            return;
                        }
                    }
                }

                if (await checkYoutubeVideoHash()) return;

                isNavigatingForwardRef.current = true;
                setPreviousStep(currentStep);
                setCurrentStep(3);
            }
        } else if (currentStep === 3) {
            if (isTextOnly) {
                // Text only step 3: schedule validation (media step is skipped)
                if (hasScheduleAfterOpenHouse()) {
                    toast.error(
                        <CustomToast
                            message="Scheduled date/time must be before the open house date."
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                    return;
                }
                if (isCompleteDisabled()) {
                    toast.error(
                        <CustomToast
                            message="Scheduled post date/time must be in the future."
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                    return;
                }
                setPreviousStep(currentStep);
                setCurrentStep(4);
            } else if (hasContentStep) {
                // Content-step layout, step 3: media selection
                if (
                    chosenPlatforms.instagram ||
                    chosenPlatforms.pinterest ||
                    chosenPlatforms.google ||
                    isVideo
                ) {
                    if (selectedListing?.listingType.toLowerCase().replace(/\s/g, '') !== "leadgeneration") {
                        if (!postCaptionedVideo && selectedMedia.length === 0) {
                            toast.error(
                                <CustomToast
                                    message="Please select media for posts."
                                    type="error"
                                />,
                                { autoClose: 3000 }
                            );
                            return;
                        }
                    }
                }

                if (await checkYoutubeVideoHash()) return;

                isNavigatingForwardRef.current = true;
                setPreviousStep(currentStep);
                setCurrentStep(4);
            } else {
                // Non-manual flow step 3: schedule validation
                if (hasScheduleAfterOpenHouse()) {
                    toast.error(
                        <CustomToast
                            message="Scheduled date/time must be before the open house date."
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                    return;
                }
                if (isCompleteDisabled()) {
                    toast.error(
                        <CustomToast
                            message="Scheduled post date/time must be in the future."
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                    return;
                }

                setPreviousStep(currentStep);
                setCurrentStep(4);
            }
        } else if (currentStep === 4) {
            if (isTextOnly) {
                // Text only step 4: preview/review — submit
                handleCreatePost();
            } else if (hasContentStep) {
                // Content-step layout, step 4: schedule validation
                if (hasScheduleAfterOpenHouse()) {
                    toast.error(
                        <CustomToast
                            message="Scheduled date/time must be before the open house date."
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                    return;
                }
                if (isCompleteDisabled()) {
                    toast.error(
                        <CustomToast
                            message="Scheduled post date/time must be in the future."
                            type="error"
                        />,
                        { autoClose: 3000 }
                    );
                    return;
                }

                setPreviousStep(currentStep);
                setCurrentStep(5);
            } else {
                handleCreatePost();
            }
        } else if (currentStep === 5) {
            // Manual flow (non-text-only) only — step 5 is the preview/review step
            handleCreatePost();
        }
    };

    const handlePrevious = () => {
      // Add Post page: Back on the first step returns to the page's listing screen.
      if (currentStep === 0 && onBackFromStart) {
        onBackFromStart();
        return;
      }
      // My Niche Video: no Back while the step is thinking, searching or writing.
      if (isNicheVideo && currentStep === steps.topic && nicheStepBusy) return;
      // Lead Generation: If on Step 3, and the previous step was 4, show confirmation overlay
      const isLeadGen =
        selectedListing?.listingType &&
        selectedListing.listingType.toLowerCase().replace(/\s/g, "") ===
          "leadgeneration";
      if (isLeadGen && currentStep === 3 && previousStep === 4) {
        setShowStepBackConfirm(true);
        return;
      }
      // If this was triggered by Radix Dialog's onOpenChange during forward navigation, skip
      if (isNavigatingForwardRef.current) {
        isNavigatingForwardRef.current = false;
        return;
      }
      // Media Selection: confirm before clearing (step index: see wizardSteps)
      // Text only has no media step so mediaStep is -1 (never triggers)
      const mediaStep = steps.media;
      if (currentStep === mediaStep && selectedMedia.length > 0) {
        setShowClearMediaConfirm(true);
        return;
      }
      // Adhoc + AI enhance: warn the user that content will be regenerated
      const reviewStep2 = steps.review;
      if (isAdHoc && aiEnhance && currentStep === reviewStep2) {
        setShowAiRegenConfirm(true);
        return;
      }
      // When navigating back from the review step, reset content sync so the
      // re-population effect runs again with the latest generalInstructions.
      const reviewStep = steps.review;
      if (currentStep === reviewStep) {
        setShouldSyncParentState(false);
        setGeneratePosts(false);
      }

      if (currentStep > 0) {
        if (currentStep === 4) {
          // For MyBrand + YouTube: keep generatePosts=true so that platform
          // add/remove alone does NOT re-trigger mybrand/generate-content.
          // Re-generation is handled exclusively by shouldTriggerMyBrandGen
          // (set when emojis/usePrice change in step 3).
          const isMyBrand =
            selectedListing?.listingType
              ?.toLowerCase()
              .replace(/\s/g, "") === "mybrand";
          const isYoutubeSelected = selectedPlatforms.some(
            (p) => p.id.toLowerCase() === "youtube"
          );
          if (!(isAdHoc && isMyBrand && isYoutubeSelected)) {
            setGeneratePosts(false);
            isGeneratingRef.current = false;
          }
        }
        setPreviousStep(currentStep);
        setCurrentStep(currentStep - 1);
      }
    };

    const isCompleteDisabled = () => {
        
        if (selectedPlatforms.length === 0) {
            return false; // Don't block if no platforms selected - this should be handled by platform selection validation
        }
        
        for (const platform of selectedPlatforms) {
            const key = platform.id.toLowerCase();
            const schedule = platformSchedules[key];
            
            if (!schedule) {
                return true;
            }
            
            if (!schedule.date) {
                return true;
            }
            
            // Use getScheduledDateTime to compute the scheduled timestamp (unix)
            const scheduledFor = getScheduledDateTime(
                schedule.date,
                { hour: schedule.hour, minute: schedule.minute },
                schedule.period,
                user?.timeZone || ""
            );

            if (!scheduledFor) {
                console.debug(
                    `No scheduled timestamp for platform '${key}'. Disabling complete button.`
                );
                return true;
            }
            const scheduledDate = new Date(scheduledFor);
            if (isNaN(scheduledDate.getTime())) {
                console.debug(
                    `Invalid scheduled date for platform '${key}': ${scheduledFor}`
                );
                return true;
            }
            // My Niche Video: a passed time is moved at submit instead (spec §4.6).
            if (!isNicheVideo && scheduledDate <= new Date()) {
                console.debug(
                    `Scheduled date for platform '${key}' is in the past or current: ${scheduledDate}`
                );
                return true;
            }
        }

        return false;
    };

    const hasOverlengthContent = (): boolean => {
        for (const platform of selectedPlatforms) {
            const key = platform.id.toLowerCase() as keyof PlatformContentMap;
            const sched = platformSchedules[key];
            const content = platformContent[key]?.content || "";
            const footerLen = sched.includeFooter
                ? sched.footer?.length || 0
                : 0;
            const effectiveLimit = isManualFlow
                ? platform.characterLimit
                : (AI_CONTENT_CHAR_LIMITS[key] ?? platform.characterLimit);
            // GMB does not support a separate "comment" footer mode in our
            // wizard, so its footer is always concatenated into the post body
            // before being sent to Ayrshare. Validate the combined length
            // against the platform limit instead of two independent caps.
            // See GMB_CHARACTER_LIMIT_BUG.md for the full rationale.
            if (key === "google") {
                const FOOTER_JOINER = "\n\n";
                const totalLength =
                    content.length +
                    (footerLen > 0 ? FOOTER_JOINER.length + footerLen : 0);
                if (totalLength > effectiveLimit) {
                    return true;
                }
                continue;
            }

            // All other platforms: behavior unchanged from before this PR.
            if (
                content.length > effectiveLimit ||
                footerLen > (key === "twitter" ? 280 : 300)
            ) {
                return true;
            }
        }
        return false;
    };

    const getOverlengthPlatforms = (): string[] => {
        const over: string[] = [];
        for (const platform of selectedPlatforms) {
            const key = platform.id.toLowerCase() as keyof PlatformContentMap;
            const sched = platformSchedules[key];
            const content = platformContent[key]?.content || "";
            const effectiveLimit = isManualFlow
                ? platform.characterLimit
                : (AI_CONTENT_CHAR_LIMITS[key] ?? platform.characterLimit);

            if (key === "google") {
                // Google footer is concatenated into the post body before sending,
                // so validate combined length against the platform limit.
                const footerLen = sched?.includeFooter ? sched.footer?.length || 0 : 0;
                const FOOTER_JOINER = "\n\n";
                const totalLength =
                    content.length +
                    (footerLen > 0 ? FOOTER_JOINER.length + footerLen : 0);
                if (totalLength > effectiveLimit) over.push(platform.name);
                continue;
            }

            if (content.length > effectiveLimit) {
                over.push(platform.name);
            }
        }
        return over;
    };

    const hasInstagramHashtagViolation = (): boolean => {
        const isInstagramSelected = selectedPlatforms.some(
            (p) => p.id.toLowerCase() === "instagram"
        );
        if (!isInstagramSelected) return false;
        const content =
            platformContent["instagram" as keyof typeof platformContent]
                ?.content || "";
        return checkInstagramHashtagViolation(content, "instagram").isViolated;
    };

    useEffect(() => {
        const filtered = availablePlatforms.filter((pl) => {
            const key = pl.id.toLowerCase() as keyof SocialsConnected;
            return chosenPlatforms[key];
        });
        
        // Sort platforms by the predefined order
        const sorted = filtered.sort((a, b) => {
            const indexA = PLATFORM_ORDER.indexOf(a.id.toLowerCase());
            const indexB = PLATFORM_ORDER.indexOf(b.id.toLowerCase());
            return indexA - indexB;
        });
        
        setSelectedPlatforms(sorted);
    }, [availablePlatforms, chosenPlatforms]);

    // Detect emojis/usePrice changes in step 3 for MyBrand and trigger regeneration
    useEffect(() => {
        if (currentStep !== 3 || !isAdHoc) return;
        const isMyBrandCtx = selectedListing?.listingType?.toLowerCase().replace(/\s/g, '') === "mybrand";
        const isYoutubeCtx = selectedPlatforms.some(p => p.id.toLowerCase() === 'youtube');
        if (!isMyBrandCtx || !isYoutubeCtx) return;

        const ytSchedule = platformSchedules["youtube"];
        const snapshot = JSON.stringify({
            emojis: ytSchedule?.emojis ?? true,
            usePrice: ytSchedule?.usePrice ?? true,
            contentLength: ytSchedule?.contentLength,
            // A different voice is a different piece of content, so it
            // has to invalidate the generated draft like the other inputs do.
            audience: contentAudience,
        });

        if (myBrandSettingsSnapshotRef.current === null) {
            myBrandSettingsSnapshotRef.current = snapshot;
            return;
        }
        if (snapshot === myBrandSettingsSnapshotRef.current) return;

        myBrandSettingsSnapshotRef.current = snapshot;
        setGeneratePosts(false);
        isGeneratingRef.current = false;
        generationSnapshotRef.current = null;
        setShouldTriggerMyBrandGen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [platformSchedules, currentStep]);

    useEffect(() => {
        const normalize = (map: PlatformContentMap) => {
            Object.values(map).forEach((slot) => {
                slot.content = slot.content.replace(/\r?\n/g, "\n\n");
            });
        };

        console.log("platformContent", platformContent);
        // console.log("generalInstructions", generalInstructions);
        

        const generateVideoPosts = async () => {
            try {
                if (aiEnhance) {
                    const headers = await getAuthHeader();
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                    const dto = {
                        campaignId: listing?.campaignId || "",
                        content: generalInstructions,
                        media: selectedMedia,
                        platforms: selectedPlatforms.map((p) => {
                            const name =
                                p.id.charAt(0).toUpperCase() + p.id.slice(1);
                            const key =
                                p.id.toLowerCase() as keyof PlatformContentMap;
                            const contentLength =
                                platformSchedules[key]?.contentLength;
                            const emojis =
                                platformSchedules[key]?.emojis || false;
                            const usePrice =
                                platformSchedules[key]?.usePrice || false;

                            return { name, contentLength, emojis, usePrice };
                        }),
                        type: videoPostType,
                        schoolDistrict: schoolDistrict || "",
                        tags: tags.length > 0 ? tags : [],
                    };

                    const response = await axios.post(
                        `${backendUrl}/post/video`,
                        dto,
                        { headers }
                    );

                    if (response.status === 200 || response.status === 201) {
                        setGeneratePosts(true);

                        const contentData = response.data;
                        const fixedContent: PlatformContentMap = {
                            facebook: contentData.Facebook || {
                                content: "",
                            },
                            instagram: contentData.Instagram || {
                                content: "",
                            },
                            twitter: contentData.Twitter || {
                                content: "",
                            },
                            linkedin: contentData.LinkedIn || {
                                content: "",
                            },
                            pinterest: contentData.Pinterest || {
                                content: "",
                            },
                            google: contentData.Google || {
                                content: "",
                            },
                            youtube: contentData.YouTube || {
                                content: "",
                            },
                        };
                        setPlatformContent(fixedContent);
                    } else {
                        throw new Error("Failed to generate posts.");
                    }
                } else {
                    const contentData = selectedPlatforms.reduce(
                        (acc, platform) => {
                            let key = "";
                            const limit =
                                platform.characterLimit -
                                (platformSchedules[platform.name.toLowerCase()]
                                    ?.footer?.length || 0);

                            console.log("limit: ", limit);

                            switch (platform.id) {
                                case SocialPlatforms.Facebook:
                                    key = "facebook";
                                    break;
                                case SocialPlatforms.Instagram:
                                    key = "instagram";
                                    break;
                                case SocialPlatforms.Twitter:
                                    key = "twitter";
                                    break;
                                case SocialPlatforms.LinkedIn:
                                    key = "linkedin";
                                    break;
                                case SocialPlatforms.Pinterest:
                                    key = "pinterest";
                                    break;
                                case SocialPlatforms.Google:
                                    key = "gmb";
                                    break;
                                default:
                                    key = platform.name;
                            }
                            acc[key as keyof PlatformContentMap] = {
                                content: trimContentToCharacterLimit(
                                    generalInstructions,
                                    limit
                                ),
                            };
                            return acc;
                        },
                        {} as PlatformContentMap
                    );
                    normalize(contentData);
                    setPlatformContent(contentData);
                }
            } catch (error) {
                console.error("Error generating posts:", error);
                const fallback =
                    "Failed to generate posts. Please try again later.";
                const message = getApiRefusalMessage(error, fallback);
                toast.error(
                    <CustomToast message={message} type="error" />,
                    { autoClose: message === fallback ? 3000 : 10000 }
                );
                if (currentStep === 4) {
                    setCurrentStep(3);
                }
            } finally {
                setCreating(false);
            }
        };

        const trimContentToCharacterLimit = (
            text: string,
            limit: number
        ): string => {
            if (!text) return "";

            const sanitized = text.trim();
            const ensured =
                sanitized.endsWith(".") || sanitized.endsWith("\n")
                    ? sanitized
                    : sanitized + ".";

            // Split by line (preserve bullet format and headers)
            const lines = ensured
                .split(/\r?\n/)
                .map((line) => line.trim())
                .filter((line) => line.length > 0);

            let finalText = "";
            for (const line of lines) {
                // Check if adding this line will exceed limit
                const potential =
                    finalText.length > 0 ? finalText + "\n" + line : line;
                if (potential.length > limit) break;
                finalText = potential;
            }

            // If even one line can't fit, fallback to sentence-based logic
            if (!finalText.trim()) {
                const sentences = ensured.match(/[^.!?\n]+[.!?\n]?/g) || [];

                let fallback = "";
                for (const sentence of sentences) {
                    const next = fallback + sentence;
                    if (next.length > limit) break;
                    fallback = next;
                }

                finalText = fallback || ensured.slice(0, limit);
            }

            // Final trimming and sanity check
            return finalText.trim().replace(/\n{3,}/g, "\n\n"); // remove excessive blank lines
        };

        const generateOpenHousePosts = async () => {
            try {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const dto = {
                    campaignId: listing?.campaignId || "",
                    content: generalInstructions,
                    media: selectedMedia,
                    platforms: selectedPlatforms.map((p) => {
                        const name =
                            p.id.charAt(0).toUpperCase() + p.id.slice(1);
                        const key =
                            p.id.toLowerCase() as keyof PlatformContentMap;
                        const contentLength =
                            platformSchedules[key]?.contentLength;
                        const emojis = platformSchedules[key]?.emojis || false;
                        const usePrice =
                            platformSchedules[key]?.usePrice || false;

                        return { name, contentLength, emojis, usePrice };
                    }),
                    openHouseDate: openHouseDate.toLocaleDateString("en-US"),
                    openHouseTime: `From ${startTime.hour
                        .toString()
                        .padStart(2, "0")}:${startTime.minute
                        .toString()
                        .padStart(2, "0")} ${startPeriod} to ${endTime.hour
                        .toString()
                        .padStart(2, "0")}:${endTime.minute
                        .toString()
                        .padStart(2, "0")} ${endPeriod}`,
                };

                const response = await axios.post(
                    `${backendUrl}/post/open-house`,
                    dto,
                    { headers }
                );

                if (response.status === 200 || response.status === 201) {
                    setGeneratePosts(true);

                    const contentData = response.data;
                    const fixedContent: PlatformContentMap = {
                        facebook: contentData.Facebook || {
                            content: "",
                        },
                        instagram: contentData.Instagram || {
                            content: "",
                        },
                        twitter: contentData.Twitter || {
                            content: "",
                        },
                        linkedin: contentData.LinkedIn || {
                            content: "",
                        },
                        pinterest: contentData.Pinterest || {
                            content: "",
                        },
                        google: contentData.Google || {
                            content: "",
                        },
                        youtube: contentData.YouTube || {
                            content: "",
                        },
                    };
                    setPlatformContent(fixedContent);
                }
            } catch (error) {
                console.error("Error generating posts:", error);
                const fallback =
                    "Failed to generate posts. Please try again later.";
                const message = getApiRefusalMessage(error, fallback);
                toast.error(
                    <CustomToast message={message} type="error" />,
                    { autoClose: message === fallback ? 3000 : 10000 }
                );
            } finally {
                setCreating(false);
            }
        };

        const generateAdHocPosts = async () => {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            try {
                const response = await axios.post(
                    `${backendUrl}/post/enhance`,
                    {
                        text: generalInstructions,
                        platforms: selectedPlatforms.map((p) => {
                            const name =
                                p.id.charAt(0).toUpperCase() + p.id.slice(1);
                            const key =
                                p.id.toLowerCase() as keyof PlatformContentMap;
                            const contentLength =
                                platformSchedules[key]?.contentLength;
                            const emojis =
                                platformSchedules[key]?.emojis || false;
                            const usePrice =
                                platformSchedules[key]?.usePrice || false;

                            return { name, contentLength, emojis, usePrice };
                        }),
                        listingId: selectedListing?.id,
                        // Without this the enhance lane rewrites a commercial
                        // piece with the residential listing prompts.
                        ...(audienceApplies && { audience: contentAudience }),
                    },
                    {
                        headers,
                    }
                );

                if (response.status !== 200) {
                    throw new Error("Failed to enhance content.");
                }

                if (response.status === 200 || response.status === 201) {
                    setGeneratePosts(true);

                    const contentData = response.data;
                    const fixedContent: PlatformContentMap = {
                        facebook: contentData.Facebook || {
                            content: "",
                        },
                        instagram: contentData.Instagram || {
                            content: "",
                        },
                        twitter: contentData.Twitter || {
                            content: "",
                        },
                        linkedin: contentData.LinkedIn || {
                            content: "",
                        },
                        pinterest: contentData.Pinterest || {
                            content: "",
                        },
                        google: contentData.Google || {
                            content: "",
                        },
                        youtube: contentData.YouTube || {
                            content: "",
                        },
                    };
                    setPlatformContent(fixedContent);
                }
            } catch (error) {
                console.error("Error enhancing content: ", error);
                const fallback =
                    "Failed to generate posts. Please try again later.";
                const message = getApiRefusalMessage(error, fallback);
                toast.error(
                    <CustomToast message={message} type="error" />,
                    { autoClose: message === fallback ? 3000 : 10000 }
                );
            } finally {
                setCreating(false);
            }
        };

        const generateMyBrandYouTubeContent = async () => {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            try {
                const response = await axios.post(
                    `${backendUrl}/mybrand/generate-content`,
                    {
                        content: generalInstructions,
                        platform: "YouTube",
                        emoji: platformSchedules["youtube"]?.emojis ?? true,
                        usePrice: platformSchedules["youtube"]?.usePrice ?? true,
                        contentLength: platformSchedules["youtube"]?.contentLength,
                        audience: contentAudience
                    },
                    { headers }
                );

                if (response.status === 200 || response.status === 201) {
                    setGeneratePosts(true);
                    const data = response.data;
                    setPlatformContent((prev) => {
                        const updated: PlatformContentMap = { ...prev };
                        updated.youtube = {
                            ...prev.youtube,
                            content: generalInstructions,
                            title: data.title ?? prev.youtube?.title,
                            hashtags: data.hashtags ?? prev.youtube?.hashtags,
                        };
                        selectedPlatforms.forEach((platform) => {
                            const key = platform.id.toLowerCase() as keyof PlatformContentMap;
                            if (key === "youtube") return;
                            const sched = platformSchedules[key];
                            const footerLen = sched?.footer?.length || 0;
                            const limit = platform.characterLimit - footerLen;
                            const trimmedContent = trimContentToCharacterLimit(generalInstructions, limit);
                            updated[key] = {
                                ...prev[key],
                                content: trimmedContent,
                                title: key === "pinterest" ? (selectedListing?.address || listing?.formattedAddress || "") : "",
                            };
                        });
                        return updated;
                    });
                }
            } catch (error) {
                console.error("Error generating MyBrand YouTube content:", error);
                const fallback =
                    "Failed to generate YouTube content. Please try again later.";
                const message = getApiRefusalMessage(error, fallback);
                toast.error(
                    <CustomToast message={message} type="error" />,
                    { autoClose: message === fallback ? 3000 : 10000 }
                );
                setGeneratePosts(true);
                setPlatformContent((prev) => {
                    const updated: PlatformContentMap = { ...prev };
                    selectedPlatforms.forEach((platform) => {
                        const key = platform.id.toLowerCase() as keyof PlatformContentMap;
                        const sched = platformSchedules[key];
                        const footerLen = sched?.footer?.length || 0;
                        const limit = platform.characterLimit - footerLen;
                        const trimmedContent = trimContentToCharacterLimit(generalInstructions, limit);
                        updated[key] = {
                            ...prev[key],
                            content: trimmedContent,
                            title:
                                key === "pinterest" || key === "youtube"
                                    ? selectedListing?.address || listing?.formattedAddress || ""
                                    : "",
                        };
                    });
                    return updated;
                });
            } finally {
                setCreating(false);
                isGeneratingRef.current = false;
            }
        };

        const reviewStep = steps.review;
        if ((currentStep === reviewStep || shouldTriggerMyBrandGen) && !generatePosts && !isGeneratingRef.current && selectedPlatforms.length > 0) {
            const currentSnapshot = JSON.stringify({
                instructions: generalInstructions,
                aiEnhance,
                audience: contentAudience,
                platforms: selectedPlatforms.map((p) => {
                    const key = p.id.toLowerCase();
                    const sched = platformSchedules[key];
                    return { id: key, contentLength: sched?.contentLength, emojis: sched?.emojis, usePrice: sched?.usePrice };
                }),
            });
            if (currentSnapshot === generationSnapshotRef.current) {
                setGeneratePosts(true);
                isGeneratingRef.current = false;
                return;
            }
            isGeneratingRef.current = true;
            generationSnapshotRef.current = currentSnapshot;
            const isMyBrandLocal = selectedListing?.listingType?.toLowerCase().replace(/\s/g, '') === "mybrand";
            const isYoutubeSelected = selectedPlatforms.some(p => p.id.toLowerCase() === 'youtube');
            if (isAdHoc && isMyBrandLocal && isYoutubeSelected) {
                setShouldTriggerMyBrandGen(false);
                setCreating(true);
                generateMyBrandYouTubeContent();
            } else if (aiEnhance) {
                setCreating(true);
                if (isVideo) {
                    generateVideoPosts();
                } else if (isAdHoc) {
                    generateAdHocPosts();
                } else if (isOpenHouse) {
                    generateOpenHousePosts();
                }
            } else if(!shouldSyncParentState) {
                const contentData = selectedPlatforms.reduce(
                    (acc, platform) => {
                        let key = "";

                        switch (platform.id) {
                            case SocialPlatforms.Facebook:
                                key = "facebook";
                                break;
                            case SocialPlatforms.Instagram:
                                key = "instagram";
                                break;
                            case SocialPlatforms.Twitter:
                                key = "twitter";
                                break;
                            case SocialPlatforms.LinkedIn:
                                key = "linkedin";
                                break;
                            case SocialPlatforms.Pinterest:
                                key = "pinterest";
                                break;
                            case SocialPlatforms.Google:
                                key = "google";
                                break;
                            case SocialPlatforms.YouTube:
                                key = "youtube";
                                break;
                            default:
                                key = platform.name;
                        }
                        // Never trim content — show full content in review step.
                        // Over-limit content is surfaced via inline warnings and the
                        // disabled Schedule Posts button; the user decides how to fix it.
                        acc[key as keyof PlatformContentMap] = {
                            content: generalInstructions,
                            title:
                                key === "pinterest" || key === "youtube"
                                    ? selectedListing?.address ||
                                      listing?.formattedAddress
                                    : "",
                        };
                        return acc;
                    },
                    {} as PlatformContentMap
                );
                setPlatformContent(contentData);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- shouldSyncParentState intentionally omitted to avoid breaking flow
    }, [
        currentStep,
        generalInstructions,
        selectedPlatforms,
        videoPostType,
        listing,
        selectedMedia,
        aiEnhance,
        schoolDistrict,
        tags,
        platformSchedules,
        generatePosts,
        endPeriod,
        endTime,
        isVideo,
        isAdHoc,
        openHouseDate,
        startPeriod,
        startTime,
        isOpenHouse,
        chosenPlatforms,
        selectedListing,
        shouldTriggerMyBrandGen,
    ]);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        const updateStatus = () => {
            const randomIndex = Math.floor(Math.random() * customStatuses.length);
            setCustomStatus(customStatuses[randomIndex]);
            const randomDelay = Math.floor(Math.random() * (3000 - 1500 + 1)) + 1500;
            timer = setTimeout(updateStatus, randomDelay);
        };
        if (creating) updateStatus();
        return () => clearTimeout(timer);
    }, [creating]);

    const handleCreatePost = async () => {
        if (hasInstagramHashtagViolation()) {
            const instagramContent =
                platformContent["instagram" as keyof typeof platformContent]?.content || "";
            const { errorMessage } = checkInstagramHashtagViolation(instagramContent, "instagram");
            toast.error(
                <CustomToast message={errorMessage} type="error" />,
                { autoClose: 4000 }
            );
            return;
        }
        if (hasOverlengthContent()) {
            toast.error(
                <CustomToast
                    message="One or more platforms have content that exceeds the character limit."
                    type="error"
                />,
                { autoClose: 3000 }
            );
            return;
        }
        if (isCompleteDisabled()) {
            toast.error(
                <CustomToast
                    message="Scheduled post date/time must be in the future."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
            return;
        } else {
            try {
                setLoading(true);
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;

                // Convert selected media IDs into a media payload similar to your other component
                const selectedMediaData = selectedMedia
                    .map((id) => {
                        let mediaItem = null;
                        let type = "";

                        // Check images
                        const imageItem = media?.images?.find(
                            (item) => item.id === id
                        );
                        if (imageItem) {
                            mediaItem = imageItem;
                            type = "Listing Image";
                        }

                        // Check videos
                        if (!mediaItem) {
                            const videoItem = media?.videos?.find(
                                (item) => item.id === id
                            );
                            if (videoItem) {
                                mediaItem = videoItem;
                                type = "Listing Video";
                            }
                        }

                        // Check assets
                        if (!mediaItem) {
                            const assetItem = media?.assets?.find(
                                (item) => item.id === id
                            );
                            if (assetItem) {
                                mediaItem = assetItem;
                                type =
                                    assetItem.assetType &&
                                    assetItem.assetType.toLowerCase() ===
                                        "video"
                                        ? "Asset Video"
                                        : "Asset Image";
                            }
                        }

                        if (mediaItem && mediaItem.url && type) {
                            return {
                                id: mediaItem.id,
                                // Listing videos carry a signed GCS URL in `url` (for display) and the
                                // clean relative filename in `name` (e.g. "UUID.mp4"). The backend
                                // expects the filename, not the full GCS URL.
                                url: type === "Listing Video"
                                    ? (mediaItem.name ?? mediaItem.url.split("?")[0])
                                    : mediaItem.url,
                                type,
                            };
                        }
                        return undefined;
                    })
                    .filter((item) => item !== undefined);

                // The video was already uploaded through the standard pipeline in CaptionedVideoMediaStep.
                // Look up the backend asset record (same as the non-captioned flow does via selectedMedia →
                // media.assets.find), so we send the real { id, url } rather than a client-generated UUID.
                const captionedVideoMediaItem = (() => {
                    if (!postCaptionedVideo || !captionedVideoUploadedFilename) return null;
                    const asset = media.assets?.find(
                        (a) => a.url === captionedVideoUploadedFilename ||
                               a.url?.endsWith(captionedVideoUploadedFilename)
                    );
                    return {
                        id: asset?.id ?? captionedVideoUploadedFilename,
                        url: asset?.url ?? captionedVideoUploadedFilename,
                        type: "Asset Video" as const,
                    };
                })();

                if (postCaptionedVideo) {
                    // Caption Video: single bulk request with all platforms in one call
                    const platforms = selectedPlatforms.map((platform) => {
                        const platformKey = platform.id.toLowerCase();
                        const scheduledAt = getScheduledDateTime(
                            platformSchedules[platformKey as keyof typeof platformSchedules].date,
                            {
                                hour: platformSchedules[platformKey as keyof typeof platformSchedules].hour,
                                minute: platformSchedules[platformKey as keyof typeof platformSchedules].minute,
                            },
                            platformSchedules[platformKey as keyof typeof platformSchedules].period,
                            user?.timeZone || ""
                        );
                        const scheduledFor = isNicheVideo ? bumpNicheScheduledFor(scheduledAt, new Date()) : scheduledAt;
                        const contentForPlatform =
                            platformContent[platformKey as keyof PlatformContentMap]?.content ?? "";
                        return {
                            platform: {
                                name: platform.id.charAt(0).toUpperCase() + platform.id.slice(1),
                            },
                            scheduledFor,
                            content: contentForPlatform || generalInstructions,
                            footer: isManualFlow ? "" : platformSchedules[platformKey as keyof typeof platformSchedules].footer,
                            footerType: isManualFlow ? (platformKey === "google" ? "in-post" : "comment") : platformSchedules[platformKey as keyof typeof platformSchedules].footerType || (platformKey === "google" ? "in-post" : "comment"),
                            includeFooter: isManualFlow ? false : platformSchedules[platformKey as keyof typeof platformSchedules].includeFooter || false,
                            threads: platformSchedules[platformKey as keyof typeof platformSchedules].threads || false,
                            emojis: platformSchedules[platformKey as keyof typeof platformSchedules].emojis || false,
                            usePrice: platformSchedules[platformKey as keyof typeof platformSchedules].usePrice || false,
                            contentLength: platformSchedules[platformKey as keyof typeof platformSchedules].contentLength || "Medium",
                        };
                    });

                    const bulkPayload = {
                        campaignId: selectedListing?.campaignId || listing?.campaignId || "",
                        media: captionedVideoMediaItem ? [captionedVideoMediaItem] : [],
                        zapcapColors: [captionBrandColors.main, captionBrandColors.secondary, captionBrandColors.accent],
                        templateId: CAPTION_STYLES.find((s) => s.id === captionStyle)?.templateId ?? "",
                        ...captionPositionField(captionPosition),
                        useCustomColorsForZapcap: captionColorIsCustom,
                        contentType: "Caption Video",
                        type: "Ad Hoc",
                        platforms,
                        // MB-1461: opt-in pre-render review — posts are HELD
                        // server-side until the transcript is approved below.
                        ...(reviewCaptions && { reviewCaptions: true }),
                        // A captioned-video post is created with no title; the
                        // ZapCap completion fills it later with no request in
                        // flight, so the voice has to be persisted with the post.
                        // Only MyBrand and My Niche Video reach this flow with the selector shown.
                        ...((isMyBrand || isNicheVideo) && { audience: contentAudience }),
                        // My Niche Video: stamps the posts and marks the topic filmed.
                        ...(isNicheVideo && nicheSelection.topicId ? { nicheTopicId: nicheSelection.topicId } : {}),
                    };

                    const bulkResponse = await axios.post<Post[] | AdhocBulkReviewResponse>(
                        `${backendUrl}/post/adhoc-bulk`,
                        bulkPayload,
                        { headers },
                    );
                    if (bulkResponse.status === 200 || bulkResponse.status === 201) {
                        const submittedReviewTaskId: string | undefined =
                            reviewCaptions && !Array.isArray(bulkResponse.data)
                                ? bulkResponse.data.reviewTaskId
                                : undefined;
                        if (submittedReviewTaskId) {
                            // MB-1461: stay IN the wizard — the waiting screen
                            // takes over until the transcript is ready.
                            dispatchReviewWait({
                                type: "submitted",
                                taskId: submittedReviewTaskId,
                            });
                        } else {
                            setShowCaptionedVideoSubmitted(true);
                        }
                    }
                } else {
                    // All other post types: per-platform requests. Platforms a
                    // previous partially-failed attempt already created are
                    // skipped so a retry cannot duplicate them.
                    const platformsToSubmit = selectedPlatforms.filter(
                        (platform) =>
                            !createdAdhocPlatformsRef.current.has(platform.id)
                    );
                    const postRequests = platformsToSubmit.map(async (platform) => {
                        const platformKey = platform.id.toLowerCase();
                        const contentForPlatform =
                            platformContent[platformKey as keyof PlatformContentMap]
                                ?.content ?? "";

                        const scheduledFor = getScheduledDateTime(
                            platformSchedules[
                                platformKey as keyof typeof platformSchedules
                            ].date,
                            {
                                hour: platformSchedules[
                                    platformKey as keyof typeof platformSchedules
                                ].hour,
                                minute: platformSchedules[
                                    platformKey as keyof typeof platformSchedules
                                ].minute,
                            },
                            platformSchedules[
                                platformKey as keyof typeof platformSchedules
                            ].period,
                            user?.timeZone || ""
                        );

                        const allowedCount = isVideo ? 1 : Math.max(platform.maxImages, platform.maxVideos);
                        const mediaForThisPlatform = selectedMediaData.slice(0, allowedCount);

                        const isLeadGeneration = selectedListing?.listingType &&
                            selectedListing.listingType.toLowerCase().replace(/\s/g, "") === "leadgeneration";

                        const payload = {
                            campaignId:
                                selectedListing?.campaignId ||
                                listing?.campaignId ||
                                "",
                            platform: {
                                name:
                                    platform.id.charAt(0).toUpperCase() +
                                    platform.id.slice(1),
                                jobId: "",
                            },
                            scheduledFor,
                            media: mediaForThisPlatform,
                            content: contentForPlatform,
                            type: isLeadGeneration || isMyBrand || isTextOnly
                                    ? "My Brand"
                                    : isVideo
                                    ? "Custom Video"
                                    : isOpenHouse
                                    ? "Open House"
                                    : "Ad Hoc",
                            contentType: isLeadGeneration
                                    ? "LeadGeneration"
                                    : isTextOnly
                                    ? "TextPost"
                                    : isMyBrand
                                    ? "MyBrand"
                                    : isVideo
                                    ? videoPostType
                                    : isOpenHouse
                                    ? "Open House"
                                    : "Ad Hoc",
                            contentLength:
                                platformSchedules[
                                    platformKey as keyof typeof platformSchedules
                                ].contentLength || 0,
                            emojis:
                                platformSchedules[
                                    platformKey as keyof typeof platformSchedules
                                ].emojis || false,
                            usePrice:
                                platformSchedules[
                                    platformKey as keyof typeof platformSchedules
                                ].usePrice || false,
                            footer: isManualFlow
                                ? ""
                                : platformSchedules[
                                    platformKey as keyof typeof platformSchedules
                                ].footer,
                            includeFooter: isManualFlow
                                ? false
                                : platformSchedules[
                                    platformKey as keyof typeof platformSchedules
                                ].includeFooter || false,
                            footerType: isManualFlow
                                ? (platformKey === "google" ? "in-post" : "comment")
                                : platformSchedules[
                                    platformKey as keyof typeof platformSchedules
                                ].footerType || (platformKey === "google" ? "in-post" : "comment"),
                            threads:
                                platformSchedules[
                                    platformKey as keyof typeof platformSchedules
                                ].threads || false,
                            title:
                                platformContent[
                                    platformKey as keyof PlatformContentMap
                                ]?.title || "",
                            // Include YouTube-specific settings
                            ...(platformKey === "youtube" && {
                                youtubeVisibility: platformSchedules["youtube"]?.youtubeVisibility ?? "public",
                                youtubeNotifySubscribers: platformSchedules["youtube"]?.notifySubscribers ?? true,
                            }),
                            // Lead generation: the graphics fields, and the sentence as image text with no body
                            ...(isLeadGeneration && {
                                background: platformGraphics[platformKey as keyof PlatformGraphicsMap]?.bgColor,
                                textColor: platformGraphics[platformKey as keyof PlatformGraphicsMap]?.textColor,
                                fontSize: platformGraphics[platformKey as keyof PlatformGraphicsMap]?.fontSize,
                                fontFamily: platformGraphics[platformKey as keyof PlatformGraphicsMap]?.fontFamily,
                                ...leadGenerationText(platformContent[platformKey as keyof PlatformContentMap] ?? {}),
                            }),
                            // Read server-side by the YouTube title fallback,
                            // which fires for a MyBrand post created without
                            // a title.
                            ...(audienceApplies && { audience: contentAudience }),
                            // A manually written post never passed through
                            // /post/enhance, which is where AI posts are
                            // charged; the api counts this one at create.
                            ...(isManualFlow && { manual: true }),
                        };

                        return axios.post(`${backendUrl}/post/adhoc`, payload, { headers });
                    });

                    const settled = await Promise.allSettled(postRequests);
                    settled.forEach((result, index) => {
                        if (result.status === "fulfilled") {
                            createdAdhocPlatformsRef.current.add(
                                platformsToSubmit[index].id
                            );
                        }
                    });
                    const failures = settled.filter(
                        (result): result is PromiseRejectedResult =>
                            result.status === "rejected"
                    );
                    if (failures.length > 0) {
                        // Surface whatever WAS created before reporting the failure.
                        if (failures.length < settled.length && getAllPosts) {
                            getAllPosts();
                        }
                        throw failures[0].reason;
                    }
                }

                // Captioned video posts are still being processed by the backend;
                // the post is not yet scheduled so we skip the success toast.
                if (!postCaptionedVideo) {
                    toast.success(
                        <CustomToast
                            message="Posts scheduled successfully."
                            type="success"
                        />,
                        { autoClose: 3000 }
                    );
                }
                if (!postCaptionedVideo) {
                    // The Add Post page passes getAllPosts for every kind (it goes to the calendar);
                    // the listing page passes none for Open House / Video Showcase, as before.
                    if (getAllPosts) {
                        getAllPosts();
                    } else if (isAdHoc) {
                        window.location.reload();
                    }
                    setIsOpen(false);
                }
            } catch (error) {
                console.error("Error creating posts: ", error);
                const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
                if (axiosError.response?.status === 410) {
                    // Dismiss the captioned-video "Captioning your video" modal
                    // so the plan-limit dialog isn't buried underneath it.
                    setShowCaptionedVideoSubmitted(false);
                    // The api's 410 message names the real limit. The captioned
                    // lane can hit either the captioning budget or the post
                    // budget, so the canned second line follows the message
                    // rather than the lane; the lane is only the fallback.
                    const limitMessage = axiosError.response?.data?.message;
                    const isCaptioningLimit = limitMessage
                        ? /caption/i.test(limitMessage)
                        : postCaptionedVideo;
                    setPlanLimitType(isCaptioningLimit ? "zapcap" : "post");
                    setPlanLimitMessage(limitMessage);
                    setPlanLimitReached(true);
                } else {
                    const backendMessage = axiosError.response?.data?.message;
                    toast.error(
                        <CustomToast
                            message={backendMessage || "Failed to schedule posts. Please try again later."}
                            type="error"
                        />,
                        {
                            autoClose: 3000,
                        }
                    );
                }
            } finally {
                setLoading(false);
            }
        }
    };


    useEffect(() => {
  // When user is on platform selection step and adds more platforms, reset sync flag
  if (currentStep === 2 && selectedPlatforms.length > 1) {
    setShouldSyncParentState(false);
  }
}, [currentStep, selectedPlatforms]);

    const resetLeadGenerationStates = () => {
      // Reset all workflow-related state for lead generation
      setSelectedListing(undefined);
      setListings([]);
      setGeneratePosts(false);
      isGeneratingRef.current = false;
      generationSnapshotRef.current = null;

      // Content and platforms
      setPlatformContent({
        facebook: { content: "", title: "" },
        instagram: { content: "", title: "" },
        twitter: { content: "", title: "" },
        linkedin: { content: "", title: "" },
        pinterest: { content: "", title: "" },
        google: { content: "", title: "" },
        youtube: { content: "", title: "" },
      });
      setPlatformSchedules(initializePlatformSchedules());
      setSelectedPlatforms([]);
      setChosenPlatforms({
        facebook: false,
        instagram: false,
        twitter: false,
        linkedin: false,
        pinterest: false,
        google: false,
      });

      // Media
      setSelectedMedia([]);
      setMedia({
        images: [],
        videos: [],
        assets: [],
      });

      // Platform graphics
      setPlatformGraphics({
        facebook: {
          bgColor: { type: "solid", color: "#000000" },
          textColor: "#FFFFFF",
          fontSize: 24,
          fontFamily: "Roboto",
        },
        instagram: {
          bgColor: { type: "solid", color: "#000000" },
          textColor: "#FFFFFF",
          fontSize: 24,
          fontFamily: "Roboto",
        },
        twitter: {
          bgColor: { type: "solid", color: "#000000" },
          textColor: "#FFFFFF",
          fontSize: 24,
          fontFamily: "Roboto",
        },
        linkedin: {
          bgColor: { type: "solid", color: "#000000" },
          textColor: "#FFFFFF",
          fontSize: 24,
          fontFamily: "Roboto",
        },
        pinterest: {
          bgColor: { type: "solid", color: "#000000" },
          textColor: "#FFFFFF",
          fontSize: 24,
          fontFamily: "Roboto",
        },
        google: {
          bgColor: { type: "solid", color: "#000000" },
          textColor: "#FFFFFF",
          fontSize: 24,
          fontFamily: "Roboto",
        },
      });

      // Instructions and tokens
      setGeneralInstructions("");
      setAiEnhance(true);
      setSchoolDistrict("");
      setTags([]);
      setVideoPostType(null);

      // Lead generation specific
      setLeadGenerationType("");
      setLeadGenerationSubtypes([]);
      setLeadGenerationPrompts([]);
      setSelectedPrompt("");
      setPromptParts([]);
      setAreTokensFilled(true);

      // Scheduling and open house
      setOpenHouseDate(() => {
        const nextDate = new Date();
        nextDate.setDate(nextDate.getDate() + 1);
        return nextDate;
      });
      setStartTime({ hour: 10, minute: 0 });
      setEndTime({ hour: 12, minute: 0 });
      setStartPeriod("AM");
      setEndPeriod("PM");

      setDefaultSettings(true);
      setHasIndividualChanges(false);
      setRememberedDefaultSchedule(null);
      setHasScheduleChangesInStep4(false);
      setShouldSyncParentState(false);
      setAvailableFooter(""); // <-- Reset footer source
      setAvailableGoogleFooter("");
      setApiFooterFallback("");
    };

   useEffect(() => {
     setGeneratePosts(false);
     isGeneratingRef.current = false;
     setPlatformContent({
       facebook: { content: "", title: "" },
       instagram: { content: "", title: "" },
       twitter: { content: "", title: "" },
       linkedin: { content: "", title: "" },
       pinterest: { content: "", title: "" },
       google: { content: "", title: "" },
       youtube: { content: "", title: "" },
     });
     setPlatformSchedules(initializePlatformSchedules());
     // Fresh schedules: My Niche Video seeds them all again.
     nicheSeededPlatformsRef.current = new Set();
     setDefaultSettings(true);
     setRememberedDefaultSchedule(null);
     setChosenPlatforms({
       facebook: false,
       instagram: false,
       twitter: false,
       linkedin: false,
       pinterest: false,
       google: false,
     });
     setSelectedPlatforms([]);
     setPlatformGraphics({
       facebook: {
         bgColor: { type: "solid", color: "#000000" },
         textColor: "#FFFFFF",
         fontSize: 24,
         fontFamily: "Roboto",
       },
       instagram: {
         bgColor: { type: "solid", color: "#000000" },
         textColor: "#FFFFFF",
         fontSize: 24,
         fontFamily: "Roboto",
       },
       twitter: {
         bgColor: { type: "solid", color: "#000000" },
         textColor: "#FFFFFF",
         fontSize: 24,
         fontFamily: "Roboto",
       },
       linkedin: {
         bgColor: { type: "solid", color: "#000000" },
         textColor: "#FFFFFF",
         fontSize: 24,
         fontFamily: "Roboto",
       },
       pinterest: {
         bgColor: { type: "solid", color: "#000000" },
         textColor: "#FFFFFF",
         fontSize: 24,
         fontFamily: "Roboto",
       },
       google: {
         bgColor: { type: "solid", color: "#000000" },
         textColor: "#FFFFFF",
         fontSize: 24,
         fontFamily: "Roboto",
       },
     });
     setSelectedMedia([]);
     setMedia({ images: [], videos: [], assets: [] });
     setGeneralInstructions("");
     //   setAiEnhance(true);
     setSchoolDistrict("");
     setTags([]);
     setVideoPostType(null);
     setLeadGenerationType("");
     setLeadGenerationSubtypes([]);
     setLeadGenerationPrompts([]);
     setSelectedPrompt("");
     setPromptParts([]);
     setAreTokensFilled(true);
     setOpenHouseDate(() => {
       const nextDate = new Date();
       nextDate.setDate(nextDate.getDate() + 1);
       return nextDate;
     });
     setStartTime({ hour: 10, minute: 0 });
     setEndTime({ hour: 12, minute: 0 });
     setStartPeriod("AM");
     setEndPeriod("PM");
     setHasIndividualChanges(false);
     setHasScheduleChangesInStep4(false);
     setShouldSyncParentState(false);
     setAvailableFooter(""); // <-- Reset footer source
     setAvailableGoogleFooter("");
     setApiFooterFallback("");
   }, [selectedListing]);

    // ------------------------------------------------------------------
    // MB-1461 — in-wizard pre-render caption review plumbing
    // ------------------------------------------------------------------
    const reviewTaskId = reviewWait.taskId;
    const reviewLive = shouldWarnOnLeave(reviewWait); // waiting | ready | approving

    // Poll/refresh the review state (also fetches the words when ready).
    // Socket is primary; this covers a missed event and the subscribe race.
    const fetchReviewStatus = useCallback(
        async (taskId: string): Promise<ZapcapReviewServerState | null> => {
            try {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const { data } = await axios.get<ZapcapReviewStatusResponse>(
                    `${backendUrl}/post/zapcap-review/${taskId}`,
                    { headers }
                );
                if (data.state === "transcript-ready") {
                    if (data.words?.length) {
                        // Never clobber in-progress edits with a refetch.
                        setReviewTokens((prev) =>
                            prev.length > 0 ? prev : tokensFromWords(data.words!)
                        );
                    }
                    dispatchReviewWait({
                        type: "poll",
                        taskId,
                        state: "transcript-ready",
                    });
                } else {
                    dispatchReviewWait({
                        type: "poll",
                        taskId,
                        state: data.state,
                    });
                }
                return data.state;
            } catch {
                // Poll fallback only — the socket remains primary.
                return null;
            }
        },
        []
    );

    // Subscribe to the review task's socket room (phase-1 zapcap-recaption
    // channel, ownership-checked server-side).
    useEffect(() => {
        if (!socket || !socketAuthenticated || !reviewTaskId || !reviewLive) return;

        const taskId = reviewTaskId;
        socket.emit("subscribeToZapcapRecaption", taskId);

        const handleProgressUpdate = (data: {
            entityId: string;
            entityType: string;
            progress: string;
            reason?: string;
        }) => {
            if (
                data.entityType !== "zapcap-recaption" ||
                data.entityId !== taskId
            ) {
                return;
            }
            if (data.progress === "transcript-ready") {
                void fetchReviewStatus(taskId);
            } else if (data.progress === "failed") {
                dispatchReviewWait({ type: "socket-failed", reason: data.reason });
            }
        };

        socket.on("progressUpdate", handleProgressUpdate);
        return () => {
            socket.off("progressUpdate", handleProgressUpdate);
            socket.emit("unsubscribeFromZapcapRecaption", taskId);
        };
    }, [socket, socketAuthenticated, reviewTaskId, reviewLive, fetchReviewStatus]);

    // Poll fallback while waiting for the transcript.
    useEffect(() => {
        if (reviewWait.phase !== "waiting" || !reviewTaskId) return;
        void fetchReviewStatus(reviewTaskId);
        const interval = setInterval(
            () => void fetchReviewStatus(reviewTaskId),
            15000
        );
        return () => clearInterval(interval);
    }, [reviewWait.phase, reviewTaskId, fetchReviewStatus]);

    // Browser-level bail (tab close / hard navigation): the generic prompt.
    // In-app closes go through handleDialogOpenChange below.
    useEffect(() => {
        if (!reviewLive) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [reviewLive]);

    const commitReviewEdit = (): void => {
        if (!reviewEditingId) return;
        const trimmed = reviewEditingValue.trim();
        setReviewTokens((prev) =>
            captionEditorReducer(
                prev,
                trimmed
                    ? { type: "edit", id: reviewEditingId, text: trimmed }
                    : { type: "delete", id: reviewEditingId }
            )
        );
        setReviewEditingId(null);
    };

    const handleReviewTokenClick = (token: CaptionToken): void => {
        if (token.deleted) {
            setReviewTokens((prev) =>
                captionEditorReducer(prev, { type: "restore", id: token.id })
            );
            return;
        }
        setReviewEditingId(token.id);
        setReviewEditingValue(token.text);
    };

    const handleReviewDeleteEditing = (): void => {
        if (!reviewEditingId) return;
        setReviewTokens((prev) =>
            captionEditorReducer(prev, { type: "delete", id: reviewEditingId })
        );
        setReviewEditingId(null);
    };

    // The user's explicit Approve — the ONLY thing that starts the render.
    const handleApproveReviewCaptions = async (): Promise<void> => {
        if (
            reviewWait.phase !== "ready" ||
            !reviewTaskId ||
            (!reviewWait.noSpeech && !canSubmit(reviewTokens))
        ) {
            return;
        }
        dispatchReviewWait({ type: "approve-started" });
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            // No spoken words: nothing to submit — approve as transcribed so
            // the render proceeds without captions.
            await axios.post(
                `${backendUrl}/post/zapcap-review/${reviewTaskId}/approve`,
                reviewWait.noSpeech ? {} : { words: toSubmitWords(reviewTokens) },
                { headers }
            );
            dispatchReviewWait({ type: "approve-succeeded" });
            // Render underway — finish exactly like the non-review flow.
            setShowCaptionedVideoSubmitted(true);
        } catch (error) {
            const status = axios.isAxiosError(error)
                ? error.response?.status
                : undefined;
            if (status === 410) {
                // The review timed out server-side — the posts are gone.
                dispatchReviewWait({ type: "approve-failed", gone: true });
                return;
            }
            dispatchReviewWait({ type: "approve-failed" });
            if (status === 409) {
                // Already settled server-side — most often approved from
                // another tab. Retrying can only 409 again, so re-read the
                // server's state and let it resolve the wizard.
                const serverState = await fetchReviewStatus(reviewTaskId);
                if (serverState === "approved") {
                    setShowCaptionedVideoSubmitted(true);
                    return;
                }
            }
            const message =
                axios.isAxiosError(error) &&
                typeof error.response?.data?.message === "string"
                    ? error.response.data.message
                    : "Couldn't approve the captions. Please try again.";
            toast.error(<CustomToast message={message} type="error" />, {
                autoClose: 5000,
            });
        }
    };

const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
        // While the captioned-video submission notice is showing the user must
        // navigate via the "Take me to calendar" button — block all other close paths.
        if (showCaptionedVideoSubmitted) return;
        // A Leave now would race the in-flight approve; it settles in a moment.
        if (reviewWait.phase === "approving") return;
        // MB-1461: leaving mid-review abandons the captioned video (the
        // server-side reaper deletes the held drafts) — warn once.
        if (reviewLive) {
            setShowReviewLeaveConfirm(true);
            return;
        }
        // Only show confirmation if a listing is selected. On the Add Post page a first step holding
        // only the card's own pick closes at once; Lead Generation's first step holds the typed hook,
        // so it keeps the confirm.
        const untouchedCardStart =
            !!preset && currentStep === 0 && !isLeadGen &&
            isCardOwnPick(selectedListing, listings, preset, !!niche.overview?.eligible);
        // Open House and Video Showcase get their listing as a prop and never set a pick, so they ask
        // once the user is past the first step.
        const listingFlowStarted = (isOpenHouse || isVideo) && currentStep > 0;
        if ((selectedListing && !untouchedCardStart) || listingFlowStarted) {
            setShowCloseConfirm(true);
        } else {
            setIsOpen(false);
        }
    } else {
        setIsOpen(true);
    }
};

    // Handler for confirming the step-back (discard changes)
    const confirmStepBack = () => {
    resetLeadGenerationStates();
    setShowStepBackConfirm(false);
    setPreviousStep(3); // Set previousStep to 3 for accurate tracking
    setCurrentStep(0);  // Go back to Step 2
    // Optionally, reset any step-3-specific state here if needed
    };

    // Handler for cancelling the step-back
    const cancelStepBack = () => {
    setShowStepBackConfirm(false);
    };

    // Handler for confirming back-navigation from last step with AI enhance
    const confirmAiRegenBack = () => {
        setShowAiRegenConfirm(false);
        setGeneratePosts(false);
        isGeneratingRef.current = false;
        generationSnapshotRef.current = null;
        const reviewStep3 = steps.review;
        const prevOfReview = steps.schedule;
        setPreviousStep(reviewStep3);
        setCurrentStep(prevOfReview);
    };

    // Handlers for clearing media confirmation when navigating back from media selection
    const confirmClearMediaBack = () => {
      setSelectedMedia([]);
      setShowClearMediaConfirm(false);
      const mediaStep = steps.media;
      const prevStep = steps.media - 1;
      setPreviousStep(mediaStep);
      setCurrentStep(prevStep);
    };

    const cancelClearMediaBack = () => {
        setShowClearMediaConfirm(false);
    };

    const cancelAiRegenBack = () => {
        setShowAiRegenConfirm(false);
    };

    const overLimitPlatforms = getOverlengthPlatforms();
    const scheduleButtonDisabled = isCompleteDisabled() || overLimitPlatforms.length > 0;
    const scheduleButtonTooltip = overLimitPlatforms.length > 0
        ? `Content exceeds character limit for: ${overLimitPlatforms.join(", ")}`
        : undefined;

    return (
      <div className="relative z-40">
        <Dialog open={open} onOpenChange={handleDialogOpenChange}>
          <DialogContent
            className={`bg-white relative flex flex-col ${
              !showAiEnhanceConfirm && !showCloseConfirm && "space-y-6"
            } ${
              isMobile
                ? "p-0 m-0"
                : creating && currentStep === finalStepIndex
                  ? "p-0"
                  : "p-4"
            } ${
              creating && currentStep === finalStepIndex
                ? "overflow-hidden"
                : ""
            }`}
            dark={true}
          >
            {planLimitReached && (
              <PlanLimitReached
                open={planLimitReached}
                onClose={() => {
                  // Dismiss only - closing the whole wizard here threw away
                  // everything entered on the earlier steps.
                  setPlanLimitReached(false);
                }}
                type={planLimitType}
                overrideMessage={planLimitMessage}
              />
            )}
            {creating && currentStep === finalStepIndex ? (
              <div className="absolute inset-0 bg-white text-primary flex items-center justify-center z-40">
                <dt className="flex items-center justify-center font-semibold text-lg">
                  <Loader className="mr-2 h-7 w-7 animate-spin text-secondary" />
                  {customStatus}
                </dt>
              </div>
            ) : null}
            {loading && <LoadingScreen />}

            {/* The submitted notice leaves only through its own button, so X is hidden there. */}
            {!showCaptionedVideoSubmitted && (
              <button
                onClick={() => handleDialogOpenChange(false)}
                className={`absolute right-4 ${
                  creating && currentStep === finalStepIndex ? "top-0" : "top-4"
                } text-gray-500 hover:text-gray-700`}
                style={{
                  zIndex: 999,
                }}
              >
                <X className="h-5 w-5" />
              </button>
            )}
            <Card
              ref={wizardCardRef}
              className={`relative w-full ${
                creating && currentStep === finalStepIndex
                  ? "p-0 rounded-none border-none shadow-none"
                  : "pt-4"
              } mt-4 ${isMobile && "border-none mt-0 pt-0"}`}
            >
              {!(creating && currentStep === finalStepIndex) &&
                !(
                  postCaptionedVideo &&
                  !isTextOnly &&
                  currentStep === steps.media
                ) && (
                <div className="flex justify-center items-center">
                  <CardTitle className="px-6 font-semibold">
                    {preset
                      ? presetDialogTitle(preset.card)
                      : isVideo
                      ? "Schedule Video Showcase Posts"
                      : isNicheVideo
                        ? "Schedule My Niche Video"
                        : isAdHoc &&
                            selectedListing?.address &&
                            selectedListing.address.toLowerCase() === "my brand"
                          ? "Schedule My Brand Posts"
                          : isAdHoc
                            ? "Schedule Ad Hoc Posts"
                            : "Schedule Open House Posts"}
                  </CardTitle>
                </div>
              )}
              <CardContent className={`relative ${isMobile ? "p-4" : "p-6"} ${creating && currentStep === finalStepIndex ? "min-h-[420px]" : ""}`}>
                <WizardStep isActive={currentStep === 0}>
                  <BasicInfoStep
                    openHouseDate={openHouseDate}
                    setOpenHouseDate={setOpenHouseDate}
                    startTime={startTime}
                    setStartTime={setStartTime}
                    endTime={endTime}
                    setEndTime={setEndTime}
                    startPeriod={startPeriod}
                    setStartPeriod={setStartPeriod}
                    endPeriod={endPeriod}
                    setEndPeriod={setEndPeriod}
                    generalInstructions={generalInstructions}
                    setGeneralInstructions={setGeneralInstructions}
                    aiEnhance={aiEnhance}
                    setAiEnhance={handleAiEnhanceChange}
                    videoPostType={videoPostType}
                    setVideoPostType={setVideoPostType}
                    schoolDistrict={schoolDistrict}
                    setSchoolDistrict={setSchoolDistrict}
                    tags={tags}
                    setTags={setTags}
                    listing={listing}
                    isVideoPost={isVideo}
                    isAdHocPost={isAdHoc}
                    isOpenHousePost={isOpenHouse}
                    hideContent={hasContentStep}
                    hideAiEnhance={contentAfterPlatforms}
                    listings={listings}
                    setListings={setListings}
                    selectedListing={selectedListing}
                    setSelectedListing={setSelectedListing}
                    confirmAiEnhanceDisable={confirmAiEnhanceDisable}
                    onTokensValidChange={setAreTokensFilled}
                    leadGenerationType={leadGenerationType}
                    setLeadGenerationType={setLeadGenerationType}
                    leadGenerationSubtypes={leadGenerationSubtypes}
                    setLeadGenerationSubtypes={setLeadGenerationSubtypes}
                    leadGenerationPrompts={leadGenerationPrompts}
                    setLeadGenerationPrompts={setLeadGenerationPrompts}
                    selectedPrompt={selectedPrompt}
                    setSelectedPrompt={setSelectedPrompt}
                    promptParts={promptParts}
                    setPromptParts={setPromptParts}
                    setAreTokensFilled={setAreTokensFilled}
                    postCaptionedVideo={postCaptionedVideo}
                    setPostCaptionedVideo={setPostCaptionedVideo}
                    reviewCaptions={reviewCaptions}
                    setReviewCaptions={setReviewCaptions}
                    contentAudience={contentAudience}
                    setContentAudience={handleContentAudienceChange}
                    nicheEligible={!!niche.overview?.eligible}
                    nicheResolved={!niche.loading}
                    preset={preset}
                  />
                  {nicheNeedsSetup && <NicheSetupPrompt compact onRecheck={niche.refresh} recheckFailed={niche.error} />}
                </WizardStep>
                <WizardStep isActive={currentStep === 1}>
                  <ChoosePlatforms
                    selectedPlatforms={chosenPlatforms}
                    setSelectedPlatforms={setChosenPlatforms}
                    onYoutubeProfileUrl={setYoutubeProfileUrl}
                    onConnectionData={setPlatformProfiles}
                    onMediaTypePreset={handleMediaTypeChange}
                    remainingPostLimit={planLimit}
                    isVideo={postCaptionedVideo || isVideo || [
                      ...(media.videos ?? []),
                      ...(media.assets ?? []).filter((a) => a.assetType === "Video"),
                    ].some((v) => selectedMedia.includes(v.id))}
                    selectedListing={selectedListing}
                    enableMediaCapability={!isLeadGen && !isTextOnly}
                    isAdHoc={isAdHoc}
                  />
                </WizardStep>
                {/* Step 2: the content step (manual flow, and the Listing Post card with the AI Enhance choice on it) */}
                {hasContentStep && (
                  <WizardStep isActive={currentStep === 2}>
                    <ManualContentStep
                      generalInstructions={generalInstructions}
                      setGeneralInstructions={setGeneralInstructions}
                      selectedPlatforms={selectedPlatforms}
                      onPasteTruncationChange={setWasPastedAndTruncated}
                      {...(contentAfterPlatforms && { aiEnhance, onAiEnhanceChange: () => handleAiEnhanceChange(!aiEnhance) })}
                    />
                  </WizardStep>
                )}

                {/* My Niche Video: Topic & script */}
                {isNicheVideo && (
                  <WizardStep isActive={currentStep === steps.topic}>
                    <NicheTopicScriptStep
                      overview={niche.overview}
                      refreshOverview={niche.refresh}
                      value={nicheSelection}
                      onChange={setNicheSelection}
                      onBusyChange={setNicheStepBusy}
                      hasRecording={!!captionedVideoFile || !!captionedVideoUploadedFilename}
                      onDiscardRecording={() => {
                        setCaptionedVideoFile(null);
                        setCaptionedVideoUploadedFilename(null);
                      }}
                      contentAudience={contentAudience}
                    />
                  </WizardStep>
                )}

                {/* Media selection (see wizardSteps) — hidden for text only */}
                <WizardStep isActive={currentStep === steps.media}>
                  {postCaptionedVideo ? (
                    <CaptionedVideoMediaStep
                      videoFile={captionedVideoFile}
                      setVideoFile={setCaptionedVideoFile}
                      captionStyle={captionStyle}
                      setCaptionStyle={setCaptionStyle}
                      captionPosition={captionPosition}
                      setCaptionPosition={setCaptionPosition}
                      onBrandColorsChange={setCaptionBrandColors}
                      onColorSourceChange={(source: ColorSource) => setCaptionColorIsCustom(source.type === "custom")}
                      onUploadedFilenameChange={setCaptionedVideoUploadedFilename}
                      uploadedFilename={captionedVideoUploadedFilename}
                      recordOnly={isNicheVideo}
                      teleprompterScript={isNicheVideo ? nicheSelection.script : undefined}
                      captionGuide={{
                        position: captionPosition,
                        style: captionStyle,
                        enabled: guidesOn,
                        onToggle: handleGuidesToggle,
                      }}
                    />
                  ) : selectedListing?.listingType
                    .toLowerCase()
                    .replace(/\s/g, "") === "leadgeneration" ? (
                    <div className="-mt-6">
                      <PostGraphicPicker
                        isOpen={true}
                        onClose={() => handlePrevious()}
                        isMobile={isMobile}
                        generalInstructions={generalInstructions}
                        bgColor={
                          platformGraphics.facebook?.bgColor || {
                            type: "solid",
                            color: "#000000",
                          }
                        }
                        textColor={
                          platformGraphics.facebook?.textColor || "#FFFFFF"
                        }
                        fontSize={platformGraphics.facebook?.fontSize || 24}
                        fontFamily={
                          platformGraphics.facebook?.fontFamily || "Roboto"
                        }
                        setBgColor={(value) =>
                          setPlatformGraphics((prev) => {
                            const updated = { ...prev };
                            const newValue =
                              typeof value === "function"
                                ? value(updated.facebook.bgColor)
                                : value;
                            Object.keys(updated).forEach((key) => {
                              updated[
                                key as keyof PlatformGraphicsMap
                              ].bgColor = newValue;
                            });
                            return updated;
                          })
                        }
                        setTextColor={(value) =>
                          setPlatformGraphics((prev) => {
                            const updated = { ...prev };
                            const newValue =
                              typeof value === "function"
                                ? value(updated.facebook.textColor)
                                : value;
                            Object.keys(updated).forEach((key) => {
                              updated[
                                key as keyof PlatformGraphicsMap
                              ].textColor = newValue;
                            });
                            return updated;
                          })
                        }
                        setFontSize={(value) =>
                          setPlatformGraphics((prev) => {
                            const updated = { ...prev };
                            const newValue =
                              typeof value === "function"
                                ? value(updated.facebook.fontSize)
                                : value;
                            Object.keys(updated).forEach((key) => {
                              updated[
                                key as keyof PlatformGraphicsMap
                              ].fontSize = newValue;
                            });
                            return updated;
                          })
                        }
                        setFontFamily={(value) =>
                          setPlatformGraphics((prev) => {
                            const updated = { ...prev };
                            const newValue =
                              typeof value === "function"
                                ? value(updated.facebook.fontFamily)
                                : value;
                            Object.keys(updated).forEach((key) => {
                              updated[
                                key as keyof PlatformGraphicsMap
                              ].fontFamily = newValue;
                            });
                            return updated;
                          })
                        }
                      />
                    </div>
                  ) : (
                    <MediaPickerDialog
                      isOpen={true}
                      onClose={() => handlePrevious()}
                      onNext={() => handleNext()}
                      media={convertMediaCollection(media)}
                      selectedMediaIds={selectedMedia}
                      setSelectedMediaIds={(ids) => setSelectedMedia(ids)}
                      isMobile={isMobile}
                      platform={Object.entries(chosenPlatforms)
                        .filter(([_, isSelected]) => isSelected)
                        .map(([platform]) => platform)
                        .join(",")}
                      listingId={
                        selectedListing?.id ||
                        listing?.id ||
                        listing?._id?.toString() ||
                        ""
                      }
                      listingType={
                        listing?.listingType || selectedListing?.listingType
                      }
                      fetchMedia={fetchMediaSilent}
                      uploadsEnabled={true}
                      isVideo={isVideo}
                      isNext={true}
                      onMediaTypeChange={handleMediaTypeChange}
                      initialMediaType={selectedMediaType}
                      isLoading={isLoadingMedia}
                      isSaving={isCheckingVideoHash}
                      showBackConfirm={showClearMediaConfirm}
                      onConfirmBack={confirmClearMediaBack}
                      onCancelBack={cancelClearMediaBack}
                      warnAboutContentEdits={shouldSyncParentState}
                      sharedMediaOnly={isMyBrand}
                      mediaTypeSegments={!isVideo}
                    />
                  )}
                </WizardStep>

                {/* Duplicate YouTube video alert */}
                <DuplicateYouTubeVideoAlert
                  open={showDuplicateVideoAlert}
                  onClose={() => setShowDuplicateVideoAlert(false)}
                />

                {/* Schedule settings (see wizardSteps) */}
                <WizardStep isActive={currentStep === steps.schedule}>
                  <PlatformSettings
                    platformSchedules={platformSchedules}
                    setPlatformSchedules={setPlatformSchedules}
                    defaultSettings={defaultSettings}
                    setDefaultSettings={setDefaultSettings}
                    hasIndividualChanges={hasIndividualChanges}
                    setHasIndividualChanges={setHasIndividualChanges}
                    postType={
                      selectedListing?.listingType &&
                      selectedListing.listingType
                        .toLowerCase()
                        .replace(/\s/g, "") === "leadgeneration"
                        ? "Lead Generation"
                        : undefined
                    }
                    hideFooter={isManualFlow}
                    sharedListing={
                      isSharedListing(listing) ||
                      isSharedListing(selectedListing) ||
                      selectedListingShared
                    }
                    currentStep={currentStep}
                    previousStep={previousStep}
                    rememberedDefaultSchedule={rememberedDefaultSchedule}
                    setRememberedDefaultSchedule={setRememberedDefaultSchedule}
                    hasScheduleChangesInStep4={hasScheduleChangesInStep4}
                    setHasScheduleChangesInStep4={setHasScheduleChangesInStep4}
                  />
                </WizardStep>

                {/* Preview & review (see wizardSteps) */}
                {!creating && (
                  <WizardStep isActive={currentStep === steps.review}>
                    <ReviewAndScheduleStep
                      listingAddress={listingAddress}
                      openHouseDate={isOpenHouse ? openHouseDate : undefined}
                      startTime={isOpenHouse ? startTime : undefined}
                      endTime={isOpenHouse ? endTime : undefined}
                      startPeriod={isOpenHouse ? startPeriod : undefined}
                      endPeriod={isOpenHouse ? endPeriod : undefined}
                      selectedPlatforms={selectedPlatforms}
                      platformContent={platformContent}
                      setPlatformContent={setPlatformContent}
                      platformSchedules={platformSchedules}
                      setPlatformSchedules={setPlatformSchedules}
                      selectedMedia={selectedMedia}
                      availableMedia={media}
                      setSelectedMedia={setSelectedMedia}
                      setShouldSyncParentState={setShouldSyncParentState}
                      shouldSyncParentState={shouldSyncParentState}
                      platformGraphics={platformGraphics}
                      setPlatformGraphics={setPlatformGraphics}
                      selectedListing={selectedListing}
                      hideValidation={true}
                      hideDatetime={false}
                      hideFooter={isManualFlow}
                      hideEditMedia={isTextOnly}
                      setHasScheduleChangesInStep4={setHasScheduleChangesInStep4}
                      youtubeProfileUrl={youtubeProfileUrl}
                      platformProfiles={platformProfiles}
                      initialMediaType={selectedMediaType}
                      onMediaTypeChange={handleMediaTypeChange}
                      isVideo={isVideo}
                      isYouTubeShort={isYouTubeShort}
                      fetchMedia={fetchMediaSilent}
                      instagramHashtagViolated={hasInstagramHashtagViolation()}
                    />
                  </WizardStep>
                )}
              </CardContent>
              {!(creating && currentStep === finalStepIndex) && (
              <CardFooter className="flex justify-between px-6 pb-6">
                <WizardNavigation
                  showPreviousOnFirstStep={!!onBackFromStart}
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                  onNext={handleNext}
                  onPrevious={handlePrevious}
                  isPreviousDisabled={isNicheVideo && currentStep === steps.topic && nicheStepBusy}
                  onComplete={async () => {
                    if (postCaptionedVideo) {
                      // Await the API — the popup is shown inside handleCreatePost
                      // only when the response is 200/201.
                      await handleCreatePost();
                    } else {
                      handleCreatePost();
                    }
                  }}
                  isNextDisabled={
                    (currentStep === 0 &&
                      selectedListing?.listingType
                        .toLowerCase()
                        .replace(/\s/g, "") === "leadgeneration" &&
                      !areTokensFilled) ||
                    (postCaptionedVideo &&
                      !isTextOnly &&
                      currentStep === steps.media &&
                      !captionedVideoUploadedFilename) ||
                    (currentStep === 0 && nicheNeedsSetup) ||
                    (currentStep === 0 && !!preset && !selectedListing) ||
                    (isNicheVideo && currentStep === steps.topic && (nicheStepBusy || !nicheSelection.script.trim()))
                  }
                  isCompleteDisabled={scheduleButtonDisabled || hasInstagramHashtagViolation()}
                  completeTooltip={scheduleButtonTooltip}
                />
              </CardFooter>
              )}
            </Card>
            {showAiEnhanceConfirm && (
              <ReconfirmOverlay
                isOpen={showAiEnhanceConfirm}
                onClose={cancelAiEnhanceDisable}
                onConfirm={confirmAiEnhanceDisable}
                title="Disable AI Enhance?"
                message="Are you sure you want to disable AI Enhance?"
                messageTwo="This will remove any AI enhancements from your post."
                confirmText="Confirm"
                reverse
              />
            )}
            {/* MB-1461: in-wizard pre-render caption review — waiting screen,
                transcript editor (phase-1 component) and failure notice. The
                "done" phase hands off to the standard submitted notice below. */}
            {reviewWait.phase !== "idle" && reviewWait.phase !== "done" && !showCaptionedVideoSubmitted && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4 z-50">
                <div className="flex w-full min-w-0 max-w-2xl max-h-[85dvh] flex-col space-y-4 overflow-y-auto overflow-x-hidden rounded-lg bg-white p-4 shadow-lg min-[640px]:p-6">
                  {reviewWait.phase === "waiting" && (
                    <>
                      <h3 className="text-primary text-lg font-bold text-center">
                        Preparing your captions for review
                      </h3>
                      <div className="flex justify-center py-2">
                        <Loader className="h-8 w-8 animate-spin text-primary" />
                      </div>
                      <p className="text-sm text-primary leading-relaxed text-center">
                        We&apos;re transcribing your video - this usually takes
                        1-2 minutes. Please stay on this screen; the transcript
                        will open here as soon as it&apos;s ready.
                      </p>
                      <div className="flex justify-center pt-2">
                        <Button
                          variant="outline"
                          onClick={() => setShowReviewLeaveConfirm(true)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </>
                  )}
                  {(reviewWait.phase === "ready" ||
                    reviewWait.phase === "approving") &&
                    reviewWait.noSpeech && (
                    <>
                      <h3 className="text-primary text-lg font-bold text-center">
                        No captions to review
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        We ran this video through our captioning process, but
                        no spoken words were detected - so there&apos;s
                        nothing to review and no captions will be added to the
                        video.
                      </p>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        You can continue and post the video as uploaded
                        (we&apos;ll add a short caption to your post for you),
                        or cancel. If this video does include speech, a version
                        with clearer audio will caption better.
                      </p>
                      <div className="flex flex-col gap-2 pt-2 min-[480px]:flex-row min-[480px]:justify-end">
                        <Button
                          variant="outline"
                          onClick={() => setShowReviewLeaveConfirm(true)}
                          disabled={reviewWait.phase === "approving"}
                        >
                          Cancel
                        </Button>
                        <Button
                          id="continue-without-captions-btn"
                          onClick={handleApproveReviewCaptions}
                          disabled={reviewWait.phase === "approving"}
                        >
                          {reviewWait.phase === "approving" ? (
                            <>
                              <Loader className="mr-2 h-4 w-4 animate-spin" />
                              Starting the render…
                            </>
                          ) : (
                            "Continue without captions"
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                  {(reviewWait.phase === "ready" ||
                    reviewWait.phase === "approving") &&
                    !reviewWait.noSpeech && (
                    <>
                      <h3 className="text-primary text-lg font-bold text-center">
                        Review your captions
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Check the transcript below and fix anything that was
                        misheard. Your video is rendered with exactly these
                        words once you approve.
                      </p>
                      <CaptionTokenEditor
                        tokens={reviewTokens}
                        editingId={reviewEditingId}
                        editingValue={reviewEditingValue}
                        setEditingValue={setReviewEditingValue}
                        onCommitEdit={commitReviewEdit}
                        onCancelEdit={() => setReviewEditingId(null)}
                        onDeleteEditing={handleReviewDeleteEditing}
                        onTokenClick={handleReviewTokenClick}
                      />
                      <div className="flex flex-col gap-2 pt-2 min-[480px]:flex-row min-[480px]:justify-end">
                        <Button
                          variant="outline"
                          onClick={() => setShowReviewLeaveConfirm(true)}
                          disabled={reviewWait.phase === "approving"}
                        >
                          Cancel
                        </Button>
                        <Button
                          id="approve-review-captions-btn"
                          onClick={handleApproveReviewCaptions}
                          disabled={
                            reviewWait.phase === "approving" ||
                            !canSubmit(reviewTokens)
                          }
                        >
                          {reviewWait.phase === "approving" ? (
                            <>
                              <Loader className="mr-2 h-4 w-4 animate-spin" />
                              Starting the render…
                            </>
                          ) : (
                            "Approve & Render Video"
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                  {reviewWait.phase === "failed" && (
                    <>
                      <h3 className="text-primary text-lg font-bold text-center">
                        {reviewWait.failureReason === "abandoned"
                          ? "This captioned video was abandoned"
                          : "Captioning failed"}
                      </h3>
                      <p className="text-sm text-primary leading-relaxed">
                        {reviewWait.failureReason === "abandoned"
                          ? "The review wasn't completed in time, so the captioned video and its draft posts were removed. No posts were created - you can start a new captioned video anytime."
                          : "We couldn't caption your video, so no posts were created. Please try again in a few minutes."}
                      </p>
                      <div className="flex justify-center">
                        <Button
                          onClick={() => {
                            dispatchReviewWait({ type: "reset" });
                            setIsOpen(false);
                          }}
                        >
                          Close
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            {/* MB-1461: leaving mid-review abandons the captioned video —
                warn once with stay/leave (the server-side reaper cleans up). */}
            {showReviewLeaveConfirm && (
              <ConfirmPopup
                open={showReviewLeaveConfirm}
                title="Abandon this captioned video?"
                message="Leaving abandons this captioned video - your posts will not be created."
                messageTwo="You can start a new captioned video anytime."
                confirmText="Leave"
                cancelText="Stay"
                onConfirm={() => {
                  setShowReviewLeaveConfirm(false);
                  // Remove the held drafts now; the server-side reaper still
                  // backstops a failed call.
                  if (reviewWait.taskId) {
                    void cancelZapcapReview(reviewWait.taskId);
                  }
                  dispatchReviewWait({ type: "reset" });
                  setIsOpen(false);
                }}
                onCancel={() => setShowReviewLeaveConfirm(false)}
                confirmVariant="destructive"
              />
            )}
            {/* Captioned-video submitted notice */}
            {showCaptionedVideoSubmitted && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4 z-50">
                <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg space-y-4">
                  <h3 className="text-primary text-lg font-bold text-center">
                    Captioning your video
                  </h3>
                  <p className="text-sm text-primary leading-relaxed">
                    Your video is processing and your post will be scheduled shortly. We’ll send a notification when it’s ready, no need to wait here.
                  </p>
                  <p className="text-sm text-primary leading-relaxed">
                    Captions are AI-transcribed, so please review your posts for accuracy before they go out.
                  </p>
                  <div className="flex justify-center">
                    <Button
                      onClick={() => {
                        if (getAllPosts) {
                            getAllPosts();
                        } else {
                            window.location.reload();
                        }
                        setShowCaptionedVideoSubmitted(false);
                        setIsOpen(false);
                        navigate("/");
                      }}
                    >
                      Take me to the calendar
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {/* Step-back confirmation overlay for lead generation */}
            {showStepBackConfirm && (
              <ConfirmPopup
                open={showStepBackConfirm}
                title="Go Back?"
                message="If you go back, your changes will be discarded."
                confirmText="Confirm"
                cancelText="Cancel"
                onConfirm={confirmStepBack}
                onCancel={cancelStepBack}
                confirmVariant="destructive"
              />
            )}
            {showAiRegenConfirm && (
              <ConfirmPopup
                open={showAiRegenConfirm}
                title="Go Back?"
                message="If you go back, the AI-generated content will be regenerated when you return."
                confirmText="Go Back"
                cancelText="Stay"
                onConfirm={confirmAiRegenBack}
                onCancel={cancelAiRegenBack}
                cancelVariant="destructive"
              />
            )}
            {showTruncationConfirm && (
              <ConfirmPopup
                open={showTruncationConfirm}
                title="Content Was Truncated"
                message="You pasted content that exceeded the character limit and it was truncated."
                messageTwo="Would you like to go back and review it, or continue with the truncated content?"
                confirmText="Continue"
                cancelText="Back"
                confirmVariant="destructive"
                onConfirm={() => {
                  setShowTruncationConfirm(false);
                  setWasPastedAndTruncated(false);
                  setPreviousStep(currentStep);
                  setCurrentStep(3);
                }}
                onCancel={() => setShowTruncationConfirm(false)}
              />
            )}
            {showCloseConfirm && (
              <ConfirmPopup
                open={showCloseConfirm}
                title="Close Panel?"
                message="Are you sure you want to close the panel?"
                messageTwo="Any unsaved changes will be lost."
                confirmText="Confirm"
                cancelText="Cancel"
                onConfirm={() => {
                  setShowCloseConfirm(false);
                  setIsOpen(false);
                }}
                onCancel={() => setShowCloseConfirm(false)}
                confirmVariant="destructive"
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
}
